import React, { useEffect, useState, useCallback, useRef } from "react";
import useTitle from "../hooks/useTitle";
import useIsMobile from "../hooks/useIsMobile";
import backend from "../backend";
import EntityLookup from "../components/design-system/EntityLookup";
import WorkOrderLookup from "../components/design-system/WorkOrderLookup";
import PieceLookup from "../components/design-system/PieceLookup";
import PurchaseOrderPDF from "../components/purchaseOrder/PurchaseOrderPDF";
import SimbolosToolbar from "../components/design-system/SimbolosToolbar";
import ComparadorRfq from "./purchases/ComparadorRfq";
import ComparadorProveedores from "./purchases/ComparadorProveedores";
import CotizarAvisos, { materialDeAviso } from "./purchases/CotizarAvisos";
import DocumentAttacher from "../components/design-system/DocumentAttacher";
import ExpedienteCompra from "./purchases/ExpedienteCompra";
import MedidaConMm from "../components/design-system/MedidaConMm";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtM   = v => v != null ? `$${Math.round(Number(v)).toLocaleString("es-CL")}` : "—";
const fmtD   = d => d ? new Date(d).toLocaleDateString("es-CL", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const padOC  = n => n ? `OCC-${String(n).padStart(4,"0")}` : "—";
const padOT  = n => n ? `OT-${String(n).padStart(4,"0")}` : null;
// Texto que pone el backend (services/avisosCompra.js, MATERIAL_POR_DEFINIR)
// cuando el aviso nace sin material conocido. No es una descripción: al crear la
// solicitud desde el aviso se muestra como placeholder y Compras escribe el material.
const MATERIAL_POR_DEFINIR = "Material por definir";
// 22-oct — Nota al proveedor estándar de las solicitudes de barras (N°15). Misma cadena que
// backend/services/lineasSolicitud.js: se propone sola cuando entra una pieza del catálogo.
const NOTA_PROVEEDOR_BARRAS = "Solicitud de cotización de barras. Medidas en pulgadas o milímetros según lo que exista comercialmente; favor cotizar el equivalente disponible e indicar plazo de entrega.";

const STATUS_META = {
  cotizando: { label:"Cotizando",     bg:"#2d2000", color:"#f39c12", border:"#4a3500" },
  cotizado:  { label:"Cotizado",      bg:"#241a35", color:"#a78bfa", border:"#3d2659" },
  pedido:    { label:"Pedido",        bg:"#0d1a2d", color:"#3498db", border:"#0d2a4a" },
  enCamino:  { label:"En camino",     bg:"#0d2a15", color:"#2ecc71", border:"#0d4020" },
  recibido:  { label:"Recibido",      bg:"#1a1a1a", color: "#999",    border:"#2e2e2e" },
  cancelado: { label:"Cancelado",     bg:"#1a1a1a", color: "#999",    border:"#2e2e2e" },
};

// Estilos uniformes para botones de acción inline.
const BTN_BASE = {
  fontSize: 11, fontWeight: 600, padding: "4px 10px",
  borderRadius: 5, cursor: "pointer", whiteSpace: "nowrap",
  fontFamily: "Arial, sans-serif",
  transition: "background .15s, border-color .15s",
};
const BTN_VARIANTS = {
  primary:   { background: "#241a35", color: "#a78bfa", border: "1px solid #3d2659" }, // Cargar cotización
  next:      { background: "#0d1a2d", color: "#3498db", border: "1px solid #0d2a4a" }, // Confirmar pedido / siguiente
  success:   { background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020" }, // Recibir
  warning:   { background: "#2d2000", color: "#f39c12", border: "1px solid #4a3500" }, // En camino / atención
  ghost:     { background: "transparent", color: "#aaa",    border: "1px solid #2e2e2e" }, // Otro proveedor
  cancel:    { background: "transparent", color: "#cbd5e1",    border: "1px solid #2e2e2e" }, // Cancelar
  danger:    { background: "transparent", color: "#e74c3c", border: "1px solid #4a0000" }, // Eliminar
};
// Botones de avisos de compra y alertas de stock (13-oct): compartidos entre la
// tabla de escritorio y las tarjetas del celular. En móvil se agrandan con
// BTN_AVISO_MOVIL para que se puedan tocar con el dedo.
const BTN_AVISO = {
  oc: { border:"none", background:"#2d0000", color:"var(--tab-rojo)", borderRadius:4, cursor:"pointer", whiteSpace:"nowrap" },
  ok: { border:"none", background:"#0d2a0d", color:"var(--tab-verde)", borderRadius:4, cursor:"pointer", whiteSpace:"nowrap" },
};
const BTN_AVISO_MOVIL = { padding:"8px 14px", fontSize:12, fontWeight:600, minHeight:40, borderRadius:8 };
// Tarjeta móvil de aviso/alerta (mismo lenguaje que las tarjetas de OC activas).
const CARD_MOVIL = { background:"var(--tab-caja)", border:"1px solid var(--tab-borde)", borderRadius:12, padding:"12px 14px" };
// 30-sep-2026 — "Respuesta del proveedor detectada": llegó a recepcion@ un correo con PDF
// del proveedor asignado (o que cita la solicitud) y todavía no se leyó. Al tocarlo se abre
// la comparación con lo solicitado.
const AlertaRespuestaProveedor = ({ respuesta, onClick }) => (
  <button type="button" onClick={onClick}
    title={`${respuesta.remitente || ""} · ${respuesta.asunto || ""}`}
    style={{ display:"block", width:"100%", textAlign:"left", marginTop:6, background:"#0d1f10", color:"#2ecc71",
             border:"1px solid #14532d", borderRadius:6, padding:"8px 10px", minHeight:40, fontSize:11, cursor:"pointer", lineHeight:1.3 }}>
    {/* 21-oct: `guardada` = el vigía de recepcion@ ya dejó el PDF en el expediente de la solicitud. */}
    <b>{respuesta.guardada ? "📎 Cotización recibida, revisar" : "✉ Respuesta del proveedor detectada"}</b> · {respuesta.asunto || "correo con PDF"}<br/>
    <span style={{ color:"#a7f3d0" }}>{respuesta.guardada ? "Guardada en el expediente · Leer y comparar →" : "Revisar y comparar con lo solicitado →"}</span>
  </button>
);

// Historial de envíos de la solicitud al proveedor (po.rfqEnvios). Se muestran también los
// que fallaron: un envío que no salió es justo el que hay que ver.
const EnviosRfq = ({ envios, fmtD }) => {
  if (!envios || !envios.length) return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:2, margin:"6px 0 2px" }}>
      {envios.slice(-3).reverse().map((e, i) => (
        <div key={i} style={{ fontSize:11, color: e.ok ? "#2ecc71" : "#e74c3c" }}>
          {e.ok ? "✔ Enviada" : "✖ Falló el envío"}{/^orden/i.test(e.asunto || "") ? " (OC)" : " (solicitud)"} a {e.a} · {fmtD(e.en)}{e.por ? ` · ${e.por}` : ""}{!e.ok && e.error ? ` · ${e.error}` : ""}
        </div>
      ))}
    </div>
  );
};

const ActionBtn = ({ variant = "ghost", onClick, title, children }) => (
  <button onClick={onClick} title={title} style={{ ...BTN_BASE, ...BTN_VARIANTS[variant] }}>
    {children}
  </button>
);

const Badge = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.cotizando;
  return (
    <span style={{
      display:"inline-block", fontSize:10, fontWeight:700, padding:"2px 7px",
      borderRadius:20, whiteSpace:"nowrap", background:s.bg, color:s.color, border:`1px solid ${s.border}`,
    }}>{s.label}</span>
  );
};

const S = {
  th: { background:"#1a1a1a", padding:"7px 10px", textAlign:"left", fontSize:10, fontWeight:700,
        color: "#999", borderBottom:"1px solid #2e2e2e", textTransform:"uppercase", letterSpacing:".04em", whiteSpace:"nowrap" },
  td: { padding:"7px 10px", color:"#bbb", borderBottom:"1px solid #252525",
        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  input: { background:"#161616", border:"1px solid #333", borderRadius:4,
           padding:"5px 8px", fontSize:12, color:"#ddd", width:"100%", boxSizing:"border-box" },
  lbl: { fontSize:10, fontWeight:700, color: "#999", textTransform:"uppercase",
         letterSpacing:".05em", display:"block", marginBottom:4 },
};

const UNITS = ["un", "kg", "m", "trozo", "kilo", "litro", "caja", "rollo"];

// F5.9 / D-T26 — Panel de certificados de material
// Lista los certificados ya cargados para esta PO + permite subir uno nuevo.
// Cuando hay items con requiereCertificado, este panel se monta automáticamente.
const CertificadoPanel = ({ purchaseOrderId }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await backend.get(`document?purchaseOrderRefs=${purchaseOrderId}&docType=certificadoMaterial`);
      setDocs(data || []);
    } catch (e) { console.error("[CertificadoPanel] load:", e); }
  }, [purchaseOrderId]);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", "certificadoMaterial");
    fd.append("purchaseOrderRefs", purchaseOrderId);
    try {
      await backend.post("document/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (err) {
      alert("Error subiendo certificado: " + (err?.response?.data?.error || err.message));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div style={{
      marginTop: 14, padding: "10px 14px",
      background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 6,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: "#f39c12",
        textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span>📋 Certificados de material</span>
        <label style={{
          fontSize: 11, color: uploading ? "#666" : "#3498db", cursor: uploading ? "default" : "pointer",
          textTransform: "none", letterSpacing: 0, fontWeight: 500,
        }}>
          {uploading ? "Subiendo…" : "+ Subir certificado"}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/*"
            onChange={handleFile}
            disabled={uploading}
            style={{ display: "none" }}
          />
        </label>
      </div>
      {docs.length === 0 ? (
        <div style={{ fontSize: 11, color: "#aaa" }}>
          Aún no se ha cargado ningún certificado para esta compra.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {docs.map(d => (
            <li key={d._id} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "5px 0", fontSize: 11, borderTop: "1px solid #252525",
            }}>
              <span style={{ color: "#bbb" }}>📄 {d.originalName}</span>
              <a href={d.url} target="_blank" rel="noreferrer" style={{
                color: "#3498db", textDecoration: "none", fontSize: 10,
              }}>ver</a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Formulario nueva OC ──────────────────────────────────────────────────────
let _keySeq = 0;
const newItem = (overrides = {}) => ({
  _key: ++_keySeq,
  description: overrides.description || "",
  material: overrides.material || "",
  quantity: overrides.quantity || 1,
  unit: overrides.unit || "un",
  pUnit: overrides.pUnit || 0,
  workOrderRef: overrides.workOrderRef || null, // { workOrderId, number, name } (forma de WorkOrderLookup)
  notes: overrides.notes || "",
  // 22-oct — Línea que nace de una pieza del catálogo: la descripción es el `material` de
  // la ficha (solo lectura) y lleva pieceRef. `sinMaterial` bloquea la línea hasta que la
  // ficha tenga material. El texto libre sigue siendo la línea sin pieceRef.
  deCatalogo: !!overrides.deCatalogo,
  pieceRef:   overrides.pieceRef || null,
  pieceSku:   overrides.pieceSku || "",
  pieceName:  overrides.pieceName || "",
  conjunto:   overrides.conjunto || "",
  sinMaterial: !!overrides.sinMaterial,
  requiereCertificado: !!overrides.requiereCertificado, // F5.9 / D-T26
  // Solo visual: placeholder propio del ítem (p. ej. "Material por definir"). No se envía al backend.
  descPlaceholder: overrides.descPlaceholder || "",
});

const emptyForm = () => ({
  provider: null,
  etaDate: "",
  internalNotes: "",
  externalNotes: "",
  items: [newItem()],
});

// D-Compras-Fase1 — Toolbar de símbolos para descripciones de material.
// Componente compartido en design-system (también usado por /catálogo).

// 22-oct — Descripción de una línea que viene del catálogo: el material de la ficha, en solo
// lectura (con el equivalente en mm discreto) y la referencia de la pieza/OT debajo. Si la ficha
// no tiene material, la línea se bloquea: se completa en el catálogo, no acá.
const DescripcionPieza = ({ item, onReleer }) => {
  const ref = [item.pieceName, item.pieceSku ? `(${item.pieceSku})` : ""].filter(Boolean).join(" ");
  if (item.sinMaterial) {
    return (
      <div style={{ border:"1px solid #e30613", borderRadius:4, padding:"6px 8px", background:"#2a1416" }}>
        <div style={{ fontSize:12, color:"#ff8a8a", fontWeight:700 }}>Sin material en la ficha · {ref}</div>
        <div style={{ fontSize:10, color:"#cbd5e1", marginTop:2 }}>
          Es una pieza del catálogo: el material no se escribe acá. Complételo en su ficha y la solicitud lo toma de ahí.
        </div>
        <div style={{ display:"flex", gap:8, marginTop:6, flexWrap:"wrap" }}>
          <a
            href={`/pieces?search=${encodeURIComponent(item.pieceSku || item.pieceName)}`}
            target="_blank" rel="noreferrer"
            style={{ fontSize:11, color:"#3498db", border:"1px solid #0d2a4a", borderRadius:4, padding:"3px 8px", textDecoration:"none" }}
          >Completar material en el catálogo ↗</a>
          <button
            type="button" onClick={onReleer}
            style={{ fontSize:11, color:"#cbd5e1", background:"transparent", border:"1px solid #444", borderRadius:4, padding:"3px 8px", cursor:"pointer" }}
          >↻ Volver a leer la ficha</button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div
        style={{ ...S.input, background:"#1a1a1a", color:"#e5e5e5", cursor:"default", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
        title="Material de la ficha del catálogo (solo lectura)"
      >
        <MedidaConMm texto={item.description} />
      </div>
      <div style={{ fontSize:10, color:"#8a8a8a", marginTop:2 }}>
        📦 {ref}{item.conjunto ? ` · del conjunto ${item.conjunto}` : ""}{item.notes ? ` · ${item.notes}` : ""}
      </div>
    </div>
  );
};

const NuevaOCForm = ({ nextNumber, onSave, onCancel, onPreview, initialItems }) => {
  // Rama móvil (26-ago): los ítems se apilan con label propio por campo,
  // en vez de la fila de columnas pensada para desktop.
  const isMobile = useIsMobile();
  const [form,   setForm]   = useState(() => ({
    ...emptyForm(),
    items: initialItems?.length ? initialItems : [newItem()],
  }));
  const [saving, setSaving] = useState(false);
  // D-Compras-Fase1 — Tracking del input de descripción con foco para
  // poder insertar símbolos (Ø, □, ▭, etc.) en la posición del cursor.
  const activeDescRef = useRef(null);
  const [activeDescItemId, setActiveDescItemId] = useState(null);
  // 22-oct — Selector "Pieza del catálogo": pieza (SKU/descripción) y OT opcional. Las líneas
  // las arma el backend (GET purchaseOrder/lineas-desde-piezas) con el material de la ficha.
  const [pickPiece, setPickPiece] = useState(null);
  const [pickWo, setPickWo] = useState(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickError, setPickError] = useState("");

  const setF = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const agregarDesdePiezas = async ({ pieceIds }) => {
    setPickLoading(true); setPickError("");
    try {
      const params = new URLSearchParams();
      if (pieceIds.length) params.set("pieces", pieceIds.join(","));
      if (pickWo?.workOrderId) params.set("workOrder", pickWo.workOrderId);
      const { data } = await backend.get(`purchaseOrder/lineas-desde-piezas?${params.toString()}`);
      const yaEstan = new Set(form.items.filter(it => it.pieceRef).map(it => String(it.pieceRef)));
      const todas = (data?.lineas || []);
      const nuevas = todas
        .filter(l => !yaEstan.has(String(l.pieceRef)))
        // la OT que origina viene por línea (solo si la pieza está en esa OT)
        .map(l => newItem({ ...l, deCatalogo: true, workOrderRef: l.workOrderRef && data.workOrder ? data.workOrder : null }));
      if (!todas.length) { setPickError(pieceIds.length ? "No se obtuvo ninguna línea para esa pieza." : "La OT no tiene piezas del catálogo."); return; }
      if (!nuevas.length) { setPickError("Esa pieza ya está en la solicitud."); return; }
      setForm(prev => ({
        ...prev,
        // la línea vacía inicial sobra cuando entran piezas
        items: [...prev.items.filter(it => it.deCatalogo || it.description.trim()), ...nuevas],
        externalNotes: prev.externalNotes || NOTA_PROVEEDOR_BARRAS,
      }));
      setPickPiece(null);
    } catch (e) {
      setPickError(e.response?.data?.error || e.message);
    } finally {
      setPickLoading(false);
    }
  };

  // Tras completar el material en el catálogo: vuelve a leer SOLO esa ficha.
  const releerPieza = async (item) => {
    try {
      const { data } = await backend.get(`purchaseOrder/lineas-desde-piezas?pieces=${item.pieceRef}`);
      const l = (data?.lineas || []).find(x => x.pieceRef === item.pieceRef) || data?.lineas?.[0];
      if (!l) { alert("La pieza ya no está en el catálogo."); return; }
      setForm(prev => ({
        ...prev,
        items: prev.items.map(it => it._key === item._key ? { ...it, description: l.description, sinMaterial: l.sinMaterial, notes: it.notes || l.notes } : it),
      }));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const updateItem = (key, field, val) =>
    setForm(prev => ({
      ...prev,
      items: prev.items.map(it => it._key === key ? { ...it, [field]: val } : it),
    }));

  const addItem   = () => setForm(prev => ({ ...prev, items: [...prev.items, newItem()] }));
  const removeItem = key => setForm(prev => ({ ...prev, items: prev.items.filter(it => it._key !== key) }));

  const handleSubmit = async () => {
    if (!form.provider?.entityId) { alert("Selecciona un proveedor"); return; }
    if (!form.items.length)       { alert("Agrega al menos un ítem");  return; }
    if (form.items.some(it => it.sinMaterial)) {
      alert("Hay piezas del catálogo sin material en la ficha. Complételo en el catálogo y use \"Volver a leer la ficha\" antes de crear la solicitud.");
      return;
    }
    if (form.items.some(it => !it.description.trim())) { alert("Todos los ítems necesitan descripción"); return; }

    setSaving(true);
    try {
      await onSave({
        provider:      form.provider.entityId,
        providerName:  form.provider.name,
        providerRut:   form.provider.rut,
        etaDate:       form.etaDate || undefined,
        internalNotes: form.internalNotes || undefined,
        externalNotes: form.externalNotes || undefined,
        items: form.items.map(it => ({
          description:  it.description.trim(),
          material:     it.material || undefined,
          quantity:     Number(it.quantity) || 1,
          unit:         it.unit,
          pUnit:        Number(it.pUnit) || 0,
          workOrderRef: it.workOrderRef?.workOrderId || undefined,
          pieceRef:     it.pieceRef || undefined, // 22-oct: el backend re-lee el material de la ficha
          notes:        it.notes || undefined,
          requiereCertificado: !!it.requiereCertificado,
        })),
      });
      setForm(emptyForm());
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    onPreview?.({
      order: {
        number: nextNumber,
        date: new Date(),
        etaDate: form.etaDate || undefined,
        items: form.items,
        externalNotes: form.externalNotes,
      },
      provider: form.provider
        ? {
            name:    form.provider.name,
            rut:     form.provider.rut,
            phone:   form.provider.phone,
            email:   form.provider.email,
            address: form.provider.address,
          }
        : null,
    });
  };

  return (
    <div style={{ background:"#242424", border:"1px solid #2e2e2e", borderRadius:8, padding:"16px 20px", marginBottom:16 }}>
      <div style={{ fontSize:11, fontWeight:800, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:6 }}>
        Nueva solicitud de cotización — {nextNumber ? padOC(nextNumber) : "…"}
      </div>
      <div style={{ fontSize:10, color: "#cbd5e1", marginBottom:14 }}>
        Estás creando un RFQ — el proveedor te enviará los precios después.
        Cuando los tengas, podés cargarlos desde la lista con el botón "✓ Cargar cotización".
      </div>

      {/* Header: proveedor + ETA */}
      <div style={{ display:"flex", gap:12, marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ flex:"2 1 240px" }}>
          <label style={S.lbl}>Proveedor *</label>
          <EntityLookup
            type="provider"
            value={form.provider ? { entityId: form.provider.entityId, name: form.provider.name } : null}
            onChange={v => setF("provider", v)}
            placeholder="Buscar proveedor..."
            allowCreate
          />
        </div>
        <div style={{ flex:"1 1 140px" }}>
          <label style={S.lbl}>Fecha requerida</label>
          <input type="date" value={form.etaDate} onChange={e => setF("etaDate", e.target.value)} style={S.input} />
        </div>
      </div>

      {/* Items */}
      <div style={{ border:"1px solid #2e2e2e", borderRadius:6, overflow:"hidden", marginBottom:14 }}>
        {/* D-Compras-Fase1 — Toolbar de símbolos para insertar en la descripción
            del ítem que esté con foco. Útil para describir material:
            "Barra Ø50 mm", "Plancha □ 200×200×6", "Tubo ▢ 40×40×3", etc. */}
        <SimbolosToolbar
          onInsert={sym => {
            const ref = activeDescRef.current;
            if (!ref?.input || !ref?.itemKey) return;
            const inp = ref.input;
            const start = inp.selectionStart ?? inp.value.length;
            const end   = inp.selectionEnd   ?? inp.value.length;
            const newValue = inp.value.slice(0, start) + sym + inp.value.slice(end);
            updateItem(ref.itemKey, "description", newValue);
            // restaurar foco + posición cursor después del símbolo
            requestAnimationFrame(() => {
              inp.focus();
              const pos = start + sym.length;
              inp.setSelectionRange(pos, pos);
            });
          }}
          targetLabel={activeDescItemId ? `ítem ${activeDescItemId}` : "ningún ítem (haz click primero)"}
        />

        {/* Encabezados de columna solo en desktop — en móvil cada campo lleva su label */}
        {!isMobile && (
        <div style={{ background:"#1a1a1a", padding:"6px 10px", fontSize:10, fontWeight:700, color: "#999", textTransform:"uppercase", letterSpacing:".04em", display:"flex", gap:4 }}>
          <span style={{ flex:"3 1 200px" }}>Descripción *</span>
          <span style={{ flex:"1 0 60px" }}>Cant.</span>
          <span style={{ flex:"1 0 60px" }}>Unidad</span>
          <span style={{ flex:"2 1 130px" }}>OT que origina</span>
          <span style={{ width:50, flexShrink:0, textAlign:"center" }}>Cert.</span>
          <span style={{ width:28, flexShrink:0 }} />
        </div>
        )}

        {form.items.map((item, idx) => isMobile ? (
          /* ── Ítem móvil: campos apilados con label, mismo estado y handlers ── */
          <div key={item._key} style={{ padding:"12px 10px 14px", borderTop: idx > 0 ? "1px solid #2e2e2e" : "none" }}>
            <label style={S.lbl}>Descripción *</label>
            {item.deCatalogo ? <DescripcionPieza item={item} onReleer={() => releerPieza(item)} /> : (
              <input
                style={S.input}
                value={item.description}
                onChange={e => updateItem(item._key, "description", e.target.value)}
                onFocus={e => {
                  activeDescRef.current = { input: e.target, itemKey: item._key };
                  setActiveDescItemId(idx + 1);
                }}
                placeholder={item.descPlaceholder || "Descripción del material (Ø50 mm, □ 200×200, etc.)"}
              />
            )}
            <div style={{ display:"flex", gap:8, marginTop:8 }}>
              <div style={{ flex:1 }}>
                <label style={S.lbl}>Cant.</label>
                <input
                  style={S.input}
                  type="number" min="0" step="any"
                  value={item.quantity}
                  onChange={e => updateItem(item._key, "quantity", e.target.value)}
                />
              </div>
              <div style={{ flex:1 }}>
                <label style={S.lbl}>Unidad</label>
                <select
                  style={S.input}
                  value={item.unit}
                  onChange={e => updateItem(item._key, "unit", e.target.value)}
                >
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop:8 }}>
              <label style={S.lbl}>OT que origina</label>
              <WorkOrderLookup
                value={item.workOrderRef}
                onChange={v => updateItem(item._key, "workOrderRef", v)}
              />
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:10 }}>
              <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#b8b8b8", cursor:"pointer" }}>
                <input
                  type="checkbox"
                  checked={!!item.requiereCertificado}
                  onChange={e => updateItem(item._key, "requiereCertificado", e.target.checked)}
                  style={{ cursor:"pointer", accentColor:"#f39c12", width:18, height:18 }}
                />
                Pedir certificado
              </label>
              <button
                onClick={() => removeItem(item._key)}
                className="t-02"
                style={{ border:"1px solid #2e2e2e", background:"transparent", color:"#999", cursor:"pointer", fontSize:12, borderRadius:10, padding:"6px 14px" }}
              >× Quitar ítem</button>
            </div>
          </div>
        ) : (
          <div key={item._key} style={{ display:"flex", gap:4, padding:"6px 8px", borderTop:"1px solid #2e2e2e", alignItems:"center", flexWrap:"wrap" }}>
            <div style={{ flex:"3 1 200px" }}>
              {item.deCatalogo ? <DescripcionPieza item={item} onReleer={() => releerPieza(item)} /> : (
                <input
                  style={S.input}
                  value={item.description}
                  onChange={e => updateItem(item._key, "description", e.target.value)}
                  onFocus={e => {
                    activeDescRef.current = { input: e.target, itemKey: item._key };
                    setActiveDescItemId(idx + 1);
                  }}
                  placeholder={item.descPlaceholder || "Descripción del material (Ø50 mm, □ 200×200, etc.)"}
                />
              )}
            </div>
            <div style={{ flex:"1 0 60px" }}>
              <input
                style={S.input}
                type="number" min="0" step="any"
                value={item.quantity}
                onChange={e => updateItem(item._key, "quantity", e.target.value)}
              />
            </div>
            <div style={{ flex:"1 0 60px" }}>
              <select
                style={S.input}
                value={item.unit}
                onChange={e => updateItem(item._key, "unit", e.target.value)}
              >
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={{ flex:"2 1 130px" }}>
              <WorkOrderLookup
                value={item.workOrderRef}
                onChange={v => updateItem(item._key, "workOrderRef", v)}
              />
            </div>
            <div
              style={{ width:50, flexShrink:0, textAlign:"center" }}
              title="Solicitar certificado del proveedor (lote/colada)"
            >
              <input
                type="checkbox"
                checked={!!item.requiereCertificado}
                onChange={e => updateItem(item._key, "requiereCertificado", e.target.checked)}
                style={{ cursor:"pointer", accentColor:"#f39c12" }}
              />
            </div>
            <button
              onClick={() => removeItem(item._key)}
              style={{ width:26, height:26, border:"none", background:"transparent", color: "#999", cursor:"pointer", fontSize:16, flexShrink:0 }}
            >×</button>
          </div>
        ))}

        {/* 22-oct — Pieza del catálogo: el material sale de la ficha (regla del 14-oct), no se digita. */}
        <div style={{ padding:"8px 10px", borderTop:"1px solid #2e2e2e", background:"#1f1f1f" }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:".04em", marginBottom:6 }}>
            Pieza del catálogo · el material sale de la ficha
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
            <div style={{ flex:"2 1 220px" }}>
              <PieceLookup value={pickPiece} onChange={setPickPiece} placeholder="SKU o descripción de la pieza…" />
            </div>
            <div style={{ flex:"2 1 180px" }}>
              <WorkOrderLookup value={pickWo} onChange={setPickWo} />
            </div>
            <button
              type="button"
              disabled={!pickPiece?.pieceId || pickLoading}
              onClick={() => agregarDesdePiezas({ pieceIds: [pickPiece.pieceId] })}
              style={{ background:"transparent", border:"1px solid #f39c12", color:"#f39c12", borderRadius:4, padding:"5px 12px", fontSize:11, cursor:"pointer", opacity: (!pickPiece?.pieceId || pickLoading) ? 0.5 : 1 }}
              title="Agrega la línea de material de esta pieza (cantidad: la de la OT si la pieza está en ella)"
            >+ Agregar pieza</button>
            <button
              type="button"
              disabled={!pickWo?.workOrderId || pickLoading}
              onClick={() => agregarDesdePiezas({ pieceIds: [] })}
              style={{ background:"transparent", border:"1px solid #444", color:"#cbd5e1", borderRadius:4, padding:"5px 12px", fontSize:11, cursor:"pointer", opacity: (!pickWo?.workOrderId || pickLoading) ? 0.5 : 1 }}
              title="Una línea por cada pieza de la OT, con la cantidad de la OT"
            >+ Todas las piezas de la OT</button>
          </div>
          {pickError && <div style={{ color:"#ff8a8a", fontSize:11, marginTop:4 }}>{pickError}</div>}
        </div>

        <div style={{ padding:"8px 10px", borderTop:"1px solid #2e2e2e" }}>
          <button onClick={addItem} style={{ background:"transparent", border:"1px solid #333", color: "#cbd5e1", borderRadius:4, padding:"4px 12px", fontSize:11, cursor:"pointer" }} title="Material suelto (que no es de una pieza del catálogo)">
            + Agregar ítem (material suelto)
          </button>
          <span style={{ float:"right", fontSize:11, color: "#aaa", fontStyle:"italic", paddingRight:8 }}>
            (Precios pendientes — los carga el proveedor en su cotización)
          </span>
        </div>
      </div>

      {/* Notas */}
      <div style={{ display:"flex", gap:12, marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ flex:"1 1 240px" }}>
          <label style={S.lbl}>Notas internas (no aparecen en cotización)</label>
          <textarea
            value={form.internalNotes}
            onChange={e => setF("internalNotes", e.target.value)}
            rows={2}
            style={{ ...S.input, resize:"vertical" }}
            placeholder="Para qué se usa, OT, etc."
          />
        </div>
        <div style={{ flex:"1 1 240px" }}>
          <label style={S.lbl}>Notas para el proveedor (aparecen en cotización)</label>
          <textarea
            value={form.externalNotes}
            onChange={e => setF("externalNotes", e.target.value)}
            rows={2}
            style={{ ...S.input, resize:"vertical" }}
            placeholder="Especificaciones, tolerancias, etc."
          />
        </div>
      </div>

      {/* Acciones */}
      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", flexWrap:"wrap" }}>
        <button onClick={onCancel} style={{ background:"transparent", color: "#cbd5e1", border:"1px solid #444", borderRadius:6, padding:"7px 14px", fontSize:12, cursor:"pointer" }}>
          Cancelar
        </button>
        <button
          onClick={handlePreview}
          style={{ background:"transparent", color:"#3498db", border:"1px solid #0d2a4a", borderRadius:6, padding:"7px 14px", fontSize:12, cursor:"pointer" }}
          title="Generar PDF de la solicitud para enviar al proveedor"
        >
          ↓ Previsualizar solicitud
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{ background:"#e30613", color:"#fff", border:"none", borderRadius:6, padding:"7px 18px", fontSize:12, fontWeight:700, cursor:"pointer", opacity:saving?0.7:1 }}
        >
          {saving ? "Guardando…" : "Crear solicitud"}
        </button>
      </div>
    </div>
  );
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const Purchases = () => {
  useTitle("Compras");
  // Rama móvil (26-ago): las OC activas se listan como tarjetas expandibles
  // (acciones adentro del detalle); el resto de tabs scrollea lateral.
  const isMobile = useIsMobile();

  const [tab,         setTab]         = useState("activas");
  const [activas,     setActivas]     = useState([]);
  const [historial,   setHistorial]   = useState([]);
  const [alertas,     setAlertas]     = useState([]);
  // T1 (26-ago) — Avisos de compra de MATERIAL generados cuando en /produccion
  // se decide Fabricar una pieza y el material no alcanza para el rinde.
  // Son distintos de `alertas`: aquellas son piezas bajo su reorderPoint (stock
  // de producto terminado), estas son material que hay que comprar YA para una
  // OT concreta que se acaba de mandar a fabricar.
  const [avisos,      setAvisos]      = useState([]);
  // 13-oct — Avisos marcados para pedir precio juntos, y los que están en la hoja
  // "Solicitud de cotización" (null = cerrada). Ver purchases/CotizarAvisos.js.
  const [avisosMarcados, setAvisosMarcados] = useState([]);
  // Avisos con solicitud de cotización en curso: ya no son trabajo pendiente, pero se
  // muestran (solo lectura) para que no queden invisibles hasta que se compre.
  const [avisosCotizando, setAvisosCotizando] = useState([]);
  const [cotizarAvisos,  setCotizarAvisos]  = useState(null);
  // D-Compras-Fase2 — Selector de mes para el tab Historial.
  // "all" = todo el historial; "YYYY-MM" = solo ese mes (por receivedDate).
  // Cuando se integre SII RCV Compras, los meses anteriores se llenan
  // automático con las facturas recibidas del SII.
  const [historialMonth, setHistorialMonth] = useState("all");
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [nextNumber,  setNextNumber]  = useState(null);
  const [expandedId,   setExpandedId]   = useState(null);
  const [prefilledItems, setPrefilledItems] = useState(null);
  const [receiveId,   setReceiveId]   = useState(null);
  const [receiveForm, setReceiveForm] = useState({ invoiceNumber:"", invoiceAmount:"", receivedDate:"" });
  const [filter,      setFilter]      = useState("");
  const [pdfData,     setPdfData]     = useState(null);
  // D-Compras-Fase2 — modal para cargar precios cuando llega cotización del proveedor
  const [cargarCotizacionTarget, setCargarCotizacionTarget] = useState(null);
  // 30-sep-2026: correos de recepcion@ que parecen la respuesta del proveedor a cada
  // solicitud abierta (poId → correo). Alimenta la alerta "Respuesta del proveedor detectada".
  const [respuestasProveedor, setRespuestasProveedor] = useState({});
  const formRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, hRes, stRes, numRes, avRes, rpRes, avcRes] = await Promise.all([
        backend.get("purchaseOrder?status=cotizando,cotizado,pedido,enCamino"),
        backend.get("purchaseOrder?status=recibido,cancelado"),
        backend.get("purchaseOrder/check-low-stock"),
        backend.get("purchaseOrder/next-number"),
        backend.get("avisosCompra?estado=pendiente"),
        backend.get("purchaseOrder/respuestas-proveedor").catch(() => ({ data: {} })),
        backend.get("avisosCompra?estado=cotizando").catch(() => ({ data: [] })),
      ]);
      setAvisosCotizando(avcRes.data || []);
      setRespuestasProveedor(rpRes.data || {});
      setActivas(aRes.data    || []);
      setHistorial(hRes.data  || []);
      setAlertas([...(stRes.data?.needsPurchase || []), ...(stRes.data?.needsFabrication || []), ...(stRes.data?.missingMaterial || [])]);
      setAvisos(avRes.data    || []);
      // Lo marcado que ya no está pendiente (otra persona lo cotizó) deja de contar.
      setAvisosMarcados(prev => prev.filter(id => (avRes.data || []).some(x => x._id === id)));
      setNextNumber(numRes.data?.next || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Ciclo F5 (31-ago-2026) — de qué aviso salió esta OC. Desde el 13-oct las
  // solicitudes nacen de la hoja "Solicitud de cotización" (purchases/CotizarAvisos.js):
  // el backend guarda en el aviso sus solicitudes y, al confirmarse una como OC, liga
  // `avisoCompra.purchaseOrder` solo (services/avisosSolicitudes.js).

  const handleSave = useCallback(async (payload) => {
    const res = await backend.post("purchaseOrder", payload);
    setActivas(prev => [res.data, ...prev]);
    setNextNumber(n => (n || 1) + 1);
    setShowForm(false);
  }, []);

  const handleStatus = useCallback(async (id, status) => {
    try {
      await backend.patch(`purchaseOrder/${id}`, { status });
      setActivas(prev => prev.map(p => p._id === id ? { ...p, status } : p));
    } catch (e) { console.error(e); }
  }, []);

  const handleAsignarProveedor = useCallback(async (id, providerObj) => {
    if (!providerObj) return;
    try {
      const res = await backend.patch(`purchaseOrder/${id}`, {
        provider:     providerObj.entityId,
        providerName: providerObj.name,
        providerRut:  providerObj.rut || undefined,
      });
      setActivas(prev => prev.map(p => p._id === id ? { ...p, ...res.data } : p));
    } catch (e) {
      alert("Error: " + (e?.response?.data?.error || e.message));
    }
  }, []);

  // Eliminar definitivamente la PO (hard delete). Útil para cotizaciones mal
  // ingresadas o de prueba — evita generar "ruido" en el registro vs cancelar.
  // Solo recomendado para POs sin avanzar (cotizando/cotizado). Para POs
  // pedido/enCamino, mejor cancelar (queda en historial).
  const handleDelete = useCallback(async (po) => {
    const lbl = `OCC-${String(po.number).padStart(4,"0")}${po.providerName ? " — " + po.providerName : ""}`;
    if (!window.confirm(`¿Eliminar definitivamente ${lbl}?\n\nEsta acción NO se puede deshacer y no quedará registro.`)) return;
    try {
      await backend.delete(`purchaseOrder/${po._id}`);
      setActivas(prev => prev.filter(p => p._id !== po._id));
    } catch (e) {
      alert("Error: " + (e?.response?.data?.error || e.message));
    }
  }, []);

  // "PDF oficial" — genera el PDF en el servidor (mismo documento que Heimdall
  // envía al proveedor por Telegram) y lo abre en una pestaña nueva.
  const [pdfOficialLoadingId, setPdfOficialLoadingId] = useState(null);
  // `modo`: "ver" abre el PDF en una pestaña; "descargar" lo guarda con su nombre real
  // (SolicitudCotizacion-14.pdf / OrdenCompra-14.pdf). Antes al guardar desde la pestaña
  // el navegador le ponía el nombre del blob (un UUID), y así llegaba al proveedor.
  const handleVerPdfOficial = useCallback(async (po, modo = "ver") => {
    try {
      setPdfOficialLoadingId(po._id);
      // 21-oct: ?modo=descargar congela la solicitud (el PDF que se manda por WhatsApp es el que
      // queda en el expediente); ?modo=ver entrega el congelado si existe, o una vista previa.
      const res = await backend.get(`purchaseOrder/${po._id}/pdf?modo=${modo === "descargar" ? "descargar" : "ver"}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (modo === "descargar") {
        const cd = String(res.headers?.["content-disposition"] || "");
        const m = cd.match(/filename="?([^";]+)"?/);
        const esRfq = po.status === "cotizando" || po.status === "cotizado";
        const nombre = m ? m[1] : `${esRfq ? "SolicitudCotizacion" : "OrdenCompra"}-${po.number}.pdf`;
        const a = document.createElement("a");
        a.href = url; a.download = nombre;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      if (modo === "descargar") load(); // la ficha muestra la solicitud congelada
    } catch (e) {
      console.error("Error generando PDF oficial de la OC:", e);
      alert("No se pudo generar el PDF oficial. Reintente en unos segundos.");
    } finally {
      setPdfOficialLoadingId(null);
    }
  }, [load]);

  // ── Enviar la solicitud de cotización al proveedor por correo ─────────────
  // El servidor genera el PDF y lo manda desde recepcion@binimeliz.com (POST /enviar-rfq,
  // que exige `confirmar: true`). Acá se confirma el destinatario antes de mandar y el
  // intento queda anotado en po.rfqEnvios, salga o no.
  const [enviarRfq, setEnviarRfq] = useState(null); // { po, para, mensaje, preview, enviando, error }
  // El correo del proveedor y el texto estándar se piden al servidor al abrir el cuadro: la
  // lista de la pantalla puede traer el proveedor sin poblar (recién asignado) y el texto del
  // correo vive en un solo lugar (backend), para que lo que se ve sea lo que sale.
  const abrirEnviarRfq = useCallback(async (po) => {
    setEnviarRfq({ po, para: po.provider?.email || "", mensaje: "", preview: null, enviando: false, error: null });
    try {
      const res = await backend.get(`purchaseOrder/${po._id}/rfq-preview`);
      setEnviarRfq(f => f && f.po._id === po._id
        ? { ...f, para: f.para || res.data.para || "", preview: res.data }
        : f);
    } catch (e) {
      setEnviarRfq(f => f ? { ...f, error: e?.response?.data?.error || e.message } : f);
    }
  }, []);
  const handleEnviarRfq = useCallback(async () => {
    if (!enviarRfq) return;
    const { po, para, mensaje } = enviarRfq;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(para).trim())) {
      setEnviarRfq(f => ({ ...f, error: "Escriba un correo válido del proveedor." }));
      return;
    }
    setEnviarRfq(f => ({ ...f, enviando: true, error: null }));
    try {
      const res = await backend.post(`purchaseOrder/${po._id}/enviar-rfq`, {
        confirmar: true, para: String(para).trim(), mensaje: mensaje || undefined,
      });
      setActivas(prev => prev.map(p => p._id === po._id ? { ...p, rfqEnvios: res.data.envios } : p));
      setEnviarRfq(null);
      alert(`${po.status === "pedido" ? "Orden de compra" : "Solicitud"} N°${po.number} enviada a ${res.data.para} desde ${res.data.de || "recepcion@binimeliz.com"}.`);
    } catch (e) {
      setEnviarRfq(f => ({
        ...f, enviando: false,
        error: e?.response?.data?.error || e.message,
        codigo: e?.response?.data?.code || null,
        respaldo: e?.response?.data?.respaldo || null,
      }));
    }
  }, [enviarRfq]);

  const handleConfirmarPedido = useCallback(async (id) => {
    if (!window.confirm("¿Confirmar la compra al proveedor? Esto pasa a 'Pedido' y se imprime la OC formal.")) return;
    try {
      const res = await backend.patch(`purchaseOrder/${id}/confirmar-pedido`);
      setActivas(prev => prev.map(p => p._id === id ? res.data : p));
    } catch (e) {
      alert("Error: " + (e?.response?.data?.error || e.message));
    }
  }, []);

  const handleDuplicarOtroProveedor = useCallback(async (id) => {
    try {
      const res = await backend.post(`purchaseOrder/${id}/duplicar-para-otro-proveedor`);
      // Insertar la duplicada arriba + actualizar la original con su rfqGroup
      setActivas(prev => {
        const updated = prev.map(p =>
          p._id === id ? { ...p, rfqGroup: res.data.original.rfqGroup } : p
        );
        return [res.data.duplicada, ...updated];
      });
      alert("Cotización duplicada. Asigná el otro proveedor en la nueva PO #" + res.data.duplicada.number + ".");
    } catch (e) {
      alert("Error: " + (e?.response?.data?.error || e.message));
    }
  }, []);

  const handleReceive = useCallback(async () => {
    if (!receiveId) return;
    try {
      await backend.patch(`purchaseOrder/${receiveId}/receive`, {
        invoiceNumber: receiveForm.invoiceNumber || undefined,
        invoiceAmount: receiveForm.invoiceAmount ? Number(receiveForm.invoiceAmount) : undefined,
        receivedDate:  receiveForm.receivedDate  || undefined,
      });

      // D-Compras-Fase2 — Subir factura y certificado material si fueron adjuntados.
      // Ambos quedan vinculados a la PO via purchaseOrderRefs y aparecen en /documentos.
      const uploads = [];
      if (receiveForm.invoiceFile) {
        const fd = new FormData();
        fd.append("file", receiveForm.invoiceFile);
        fd.append("docType", "facturaRecibida");
        fd.append("purchaseOrderRefs", receiveId);
        fd.append("label", `Factura proveedor N° ${receiveForm.invoiceNumber || ""}`);
        uploads.push(backend.post("document/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      }
      if (receiveForm.certificateFile) {
        const fd = new FormData();
        fd.append("file", receiveForm.certificateFile);
        fd.append("docType", "certificadoMaterial");
        fd.append("purchaseOrderRefs", receiveId);
        fd.append("label", `Certificado material — Factura ${receiveForm.invoiceNumber || ""}`);
        uploads.push(backend.post("document/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      }
      if (uploads.length) {
        try { await Promise.all(uploads); }
        catch (uploadErr) {
          alert("Recepción guardada pero falló la subida de documentos: " + (uploadErr?.response?.data?.error || uploadErr.message) +
                "\nPodés subirlos después desde /documentos.");
        }
      }

      await load();
      setReceiveId(null);
    } catch (e) { console.error(e); alert("Error al registrar recepción"); }
  }, [receiveId, receiveForm, load]);

  const openReceive = useCallback((id) => {
    const oc = activas.find(p => p._id === id);
    setReceiveId(id);
    setReceiveForm({
      invoiceNumber: "",
      invoiceAmount: oc?.total ? String(Math.round(oc.total)) : "",
      receivedDate:  new Date().toISOString().split("T")[0],
      invoiceFile:   null,
      certificateFile: null,
    });
  }, [activas]);

  const openForm = useCallback((prefillPiece) => {
    if (prefillPiece) {
      setPrefilledItems([newItem({ description: `${prefillPiece.name}${prefillPiece.sku ? ` (${prefillPiece.sku})` : ""}`, material: prefillPiece.material || "" })]);
    } else {
      setPrefilledItems(null);
    }
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior:"smooth" }), 50);
  }, []);

  // 13-oct — "+ Cotizar" y "Cotizar seleccionados" abren la hoja "Solicitud de
  // cotización" con los materiales de los avisos ya cargados (antes abría el formulario
  // de OC en blanco). Genera una solicitud por proveedor; los avisos pasan a "cotizando"
  // y salen de esta lista sin perderse.
  const alternarAviso = useCallback((id) => {
    setAvisosMarcados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);
  const abrirCotizarMarcados = useCallback(() => {
    const sel = avisos.filter(a => avisosMarcados.includes(a._id));
    if (sel.length) setCotizarAvisos(sel);
  }, [avisos, avisosMarcados]);
  const handleCotizacionGenerada = useCallback(async (r) => {
    const ids = (cotizarAvisos || []).map(a => a._id);
    setCotizarAvisos(null);
    setAvisosMarcados(prev => prev.filter(id => !ids.includes(id)));
    const detalle = (r?.solicitudes || []).map(x => `N°${x.number}${x.proveedor ? ` (${x.proveedor})` : ""}`).join(", ");
    alert(`${r?.solicitudes?.length === 1 ? "Se generó la solicitud" : `Se generaron ${r?.solicitudes?.length || 0} solicitudes`} ${detalle}. Quedan en Activas para enviarlas al proveedor.`);
    await load();
  }, [cotizarAvisos, load]);

  // El aviso no se borra: se marca atendido y queda la constancia de que en su
  // momento faltaba material (sirve para explicar un atraso semanas después).
  const handleAtenderAviso = useCallback(async (id) => {
    // Cerrar el aviso sin OC es deliberado (p. ej. el material apareció en bodega):
    // se confirma para que un toque accidental en el celular no lo haga desaparecer.
    if (!window.confirm("¿Marcar este aviso como atendido sin crear una solicitud de compra?")) return;
    try {
      await backend.patch(`avisosCompra/${id}`, { estado: "atendido" });
      setAvisos(prev => prev.filter(a => a._id !== id));
    } catch (e) { console.error(e); }
  }, []);

  const hIn  = e => Array.from(e.currentTarget.cells).forEach(c => c.style.background = "#2a2a2a");
  const hOut = e => Array.from(e.currentTarget.cells).forEach(c => c.style.background = "");

  const filteredActivas = activas.filter(p => {
    const q = filter.toLowerCase();
    return !q ||
      (p.providerName || p.provider?.name || "").toLowerCase().includes(q) ||
      String(p.number || "").includes(q) ||
      (p.internalNotes || "").toLowerCase().includes(q);
  });

  const cotizandoN = activas.filter(p => p.status === "cotizando").length;
  const enProcesoN = activas.filter(p => p.status === "pedido" || p.status === "enCamino").length;
  const recibidasN = historial.filter(p => p.status === "recibido").length;
  // D-Compras-Fase1 — total $ comprado en el último mes (suma de invoiceAmount o total)
  const ahora       = new Date();
  const haceUnMes   = new Date(ahora.getFullYear(), ahora.getMonth() - 1, ahora.getDate());
  const compradoMes = historial
    .filter(p => p.status === "recibido" && p.receivedDate && new Date(p.receivedDate) >= haceUnMes)
    .reduce((s, p) => s + (Number(p.invoiceAmount) || Number(p.total) || 0), 0);
  const fmtCLP = n => `$${Math.round(n || 0).toLocaleString("es-CL")}`;

  return (
    // clamp: 22px lateral en desktop, ~10px en móvil (360-430px)
    <div style={{ display:"block", padding:"clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background:"#1a1a1a", minHeight:"100%", color:"#ddd", fontFamily:"Arial,sans-serif", fontSize:13, boxSizing:"border-box", width:"100%" }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:8 }}>
        <div style={{ fontSize:16, fontWeight:700, color:"#fff" }}>Compras</div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Buscar por N° OC, proveedor..."
            style={{ ...S.input, width:"min(220px, 100%)" }}
          />
          <button
            onClick={() => { setShowForm(v => { const next = !v; return next; }); }}
            style={{ background:"#e30613", color:"#fff", border:"none", borderRadius:6, padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}
          >
            {showForm ? "Cancelar" : "+ Nueva cotización"}
          </button>
        </div>
      </div>

      {/* ── Métricas (cards clickables: cambian de tab automático) ── */}
      <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
        {[
          { label:"Alertas stock", value:alertas.length + avisos.length, sub:"requieren compra",   color:"#e74c3c", goTab:"alertas" },
          { label:"Cotizando",     value:cotizandoN,     sub:"esperando precio",   color:"#f39c12", goTab:"activas" },
          { label:"En proceso",    value:enProcesoN,     sub:"pedido o en camino", color:"#3498db", goTab:"activas" },
          { label:"Recibido",      value:recibidasN,     sub:"historial",          color:"#2ecc71", goTab:"historial" },
          { label:"Comprado mes",  value:fmtCLP(compradoMes), sub:"últimos 30 días", color:"#16a085", goTab:null, isMetric:true },
        ].map(m => {
          const clickable = !!m.goTab;
          return (
            <div key={m.label}
              onClick={() => clickable && setTab(m.goTab)}
              title={clickable ? `Ir a tab "${m.goTab}"` : "Métrica de período (no clickable)"}
              style={{
                flex:"1 1 0", minWidth:140,
                background:"#242424", border:"1px solid #2e2e2e",
                borderRadius:8, padding:"12px 14px",
                cursor: clickable ? "pointer" : "default",
                opacity: m.isMetric ? 0.95 : 1,
                transition:"background .15s, border-color .15s",
              }}
              onMouseEnter={e => { if (clickable) { e.currentTarget.style.background = "#2a2a2a"; e.currentTarget.style.borderColor = m.color + "55"; } }}
              onMouseLeave={e => { if (clickable) { e.currentTarget.style.background = "#242424"; e.currentTarget.style.borderColor = "#2e2e2e"; } }}
            >
              <div style={{ fontSize:10, color: "#aaa", marginBottom:4, textTransform:"uppercase", letterSpacing:".05em" }}>{m.label}</div>
              <div style={{ fontSize: typeof m.value === "string" && m.value.length > 8 ? 18 : 24, fontWeight:700, color:m.color }}>{m.value}</div>
              <div style={{ fontSize:10, color: "#999", marginTop:2 }}>{m.sub}</div>
            </div>
          );
        })}
      </div>

      {/* ── Formulario nueva OC ── */}
      {showForm && (
        <div ref={formRef}>
          <NuevaOCForm
            key={prefilledItems ? JSON.stringify(prefilledItems[0]?._key) : "empty"}
            nextNumber={nextNumber}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setPrefilledItems(null); }}
            onPreview={setPdfData}
            initialItems={prefilledItems}
          />
        </div>
      )}

      {/* ── Tabs ── (flexWrap: en móvil bajan de línea en vez de rebalsar) */}
      <div style={{ display:"flex", borderBottom:"1px solid #2e2e2e", marginBottom:14, flexWrap:"wrap" }}>
        {[
          ["activas",  "OC Activas"],
          ["alertas",  `Alertas de stock${alertas.length + avisos.length ? ` (${alertas.length + avisos.length})` : ""}`],
          ["historial","Historial"],
          ["sinRegistro","Sin registro (SII)"],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding:"7px 16px", background:"transparent", border:"none",
            borderBottom: tab===key ? "2px solid #e30613" : "2px solid transparent",
            color: tab===key ? "#fff" : "#555",
            fontSize:12, fontWeight: tab===key ? 700 : 400, cursor:"pointer",
          }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:"32px 0", color: "#999" }}>Cargando…</div>

      ) : tab === "activas" ? (
        isMobile ? (
        /* ══════════════ OC ACTIVAS — tarjetas móviles (26-ago) ══════════════
           Misma data y handlers que la tabla desktop; el detalle (ítems,
           certificados, notas, PDFs y acciones) vive dentro de la tarjeta. */
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {filteredActivas.length === 0 && (
            <div style={{ textAlign:"center", color:"#8a8a8a", fontSize:12, padding:"24px 0", background:"#232323", border:"1px solid #2e2e2e", borderRadius:12 }}>
              Sin órdenes activas
            </div>
          )}
          {filteredActivas.map(p => {
            const isExp   = expandedId === p._id;
            const pName   = p.providerName || p.provider?.name || "";
            const nItems  = (p.items || []).length;
            return (
              <div key={p._id} className="t-02" style={{
                background: isExp ? "#262626" : "#232323",
                border: `1px solid ${isExp ? "#3a5a7a" : "#2e2e2e"}`,
                borderRadius: 12, overflow: "hidden",
              }}>
                {/* Cabecera tocable */}
                <div
                  onClick={() => setExpandedId(isExp ? null : p._id)}
                  className={isExp ? "" : "tap-card"}
                  style={{ padding:"12px 14px", cursor:"pointer", minHeight:48 }}
                >
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <span style={{ display:"inline-block", transition:"transform .15s", transform: isExp ? "rotate(90deg)" : "none", color:"#8a8a8a", flexShrink:0 }}>›</span>
                    <span style={{ fontFamily:"monospace", fontWeight:700, fontSize:13, color:"#b8b8b8", flexShrink:0 }}>{padOC(p.number)}</span>
                    <span style={{ flex:1 }} />
                    <Badge status={p.status} />
                  </div>
                  <div style={{ fontSize:14, fontWeight:600, color: pName ? "#fff" : "#f39c12", marginBottom:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", paddingLeft:22 }}>
                    {pName || "Sin proveedor asignado"}
                  </div>
                  {respuestasProveedor[p._id] && (p.status === "cotizando" || p.status === "cotizado") && (
                    <AlertaRespuestaProveedor
                      respuesta={respuestasProveedor[p._id]}
                      onClick={e => { e.stopPropagation(); setCargarCotizacionTarget(p); }}
                    />
                  )}
                  <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8, paddingLeft:22 }}>
                    <span style={{ fontSize:11, color:"#8a8a8a" }}>
                      {nItems} ítem{nItems !== 1 ? "s" : ""}{p.etaDate ? ` · ETA ${fmtD(p.etaDate)}` : ""}
                    </span>
                    <span style={{ fontSize:14, fontWeight:700, color:"#2ecc71", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" }}>{fmtM(p.total)}</span>
                  </div>
                </div>

                {/* Detalle expandido */}
                {isExp && (
                  <div style={{ borderTop:"1px solid #2e2e2e", background:"#1e1e1e", padding:"12px 14px" }}>
                    {!(p.provider || p.providerName) && (
                      <div style={{ marginBottom:12 }}>
                        <label style={S.lbl}>Asignar proveedor</label>
                        <EntityLookup
                          type="provider"
                          value={null}
                          onChange={v => handleAsignarProveedor(p._id, v)}
                          placeholder="Asignar proveedor…"
                        />
                      </div>
                    )}

                    <div style={{ fontSize:10, fontWeight:700, color:"#8a8a8a", textTransform:"uppercase", letterSpacing:".04em", marginBottom:4 }}>
                      Ítems ({nItems})
                    </div>
                    {(p.items || []).map((item, idx) => (
                      <div key={item._id || idx} style={{ padding:"8px 0", borderBottom:"1px solid #252525" }}>
                        <div style={{ fontSize:13, color:"#ddd", marginBottom:3 }}>{item.description}</div>
                        {/* 30-sep-2026 (Maxi): la nota interna de la línea dice para qué pieza (SKU) se
                            pide el material. Se ve acá; al proveedor le llega solo la descripción. */}
                        {item.notes && <div style={{ fontSize:11, color:"#a3a3a3", marginBottom:3 }}>{item.notes}</div>}
                        <div style={{ display:"flex", gap:10, flexWrap:"wrap", fontSize:11, color:"#8a8a8a" }}>
                          <span>{item.quantity} {item.unit}</span>
                          {item.pUnit ? <span>c/u {fmtM(item.pUnit)}</span> : null}
                          {item.subtotal ? <span style={{ color:"#2ecc71", fontWeight:600 }}>{fmtM(item.subtotal)}</span> : null}
                          {item.workOrderRef && typeof item.workOrderRef === "object" && (
                            <span style={{ fontFamily:"monospace", color:"#f39c12" }}>{padOT(item.workOrderRef.number)}</span>
                          )}
                          {item.requiereCertificado && <span style={{ color:"#f39c12" }}>⚠ cert.</span>}
                        </div>
                      </div>
                    ))}

                    <ExpedienteCompra po={p} onCambio={load} />

                    {(p.items || []).some(it => it.requiereCertificado) && (
                      <CertificadoPanel purchaseOrderId={p._id} />
                    )}

                    {(p.internalNotes || p.externalNotes) && (
                      <div style={{ marginTop:12 }}>
                        {p.internalNotes && (
                          <div style={{ marginBottom:8 }}>
                            <div style={{ fontSize:10, color:"#8a8a8a", marginBottom:2, textTransform:"uppercase", letterSpacing:".04em" }}>Nota interna</div>
                            <div style={{ fontSize:12, color:"#b8b8b8" }}>{p.internalNotes}</div>
                          </div>
                        )}
                        {p.externalNotes && (
                          <div>
                            <div style={{ fontSize:10, color:"#8a8a8a", marginBottom:2, textTransform:"uppercase", letterSpacing:".04em" }}>Nota al proveedor</div>
                            <div style={{ fontSize:12, color:"#b8b8b8" }}>{p.externalNotes}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* PDFs */}
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:12 }}>
                      {/* 30-sep-2026: un solo PDF (el del servidor). La vista previa del navegador
                          quedó solo para el formulario de una OC aún no guardada. */}
                      <button
                        onClick={() => handleVerPdfOficial(p)}
                        disabled={pdfOficialLoadingId === p._id}
                        className="t-02"
                        style={{ flex:1, minWidth:130, background:"transparent", color:"#2ecc71", border:"1px solid #14532d", borderRadius:10, padding:"8px 12px", fontSize:12, cursor:"pointer", opacity: pdfOficialLoadingId === p._id ? 0.6 : 1 }}
                      >
                        {pdfOficialLoadingId === p._id ? "Generando…" : `↓ PDF ${p.status === "cotizando" || p.status === "cotizado" ? "solicitud" : "OC"}`}
                      </button>
                      <button
                        onClick={() => handleVerPdfOficial(p, "descargar")}
                        disabled={pdfOficialLoadingId === p._id}
                        title="Guardar el PDF con su nombre (SolicitudCotizacion-N.pdf)"
                        style={{ background:"transparent", color:"#8a8a8a", border:"1px solid #2e2e2e", borderRadius:10, padding:"8px 12px", fontSize:12, cursor:"pointer" }}
                      >
                        💾 Guardar
                      </button>
                      {(p.status === "cotizando" || p.status === "pedido") && (
                        <button
                          onClick={() => abrirEnviarRfq(p)}
                          className="t-02"
                          title={`Enviar la ${p.status === "pedido" ? "orden de compra" : "solicitud"} con el PDF adjunto desde recepcion@binimeliz.com`}
                          style={{ flex:1, minWidth:130, background:"#0d1a2d", color:"#3498db", border:"1px solid #0d2a4a", borderRadius:10, padding:"8px 12px", fontSize:12, cursor:"pointer" }}
                        >
                          ✉ Enviar {p.status === "pedido" ? "OC" : ""} al proveedor
                        </button>
                      )}
                    </div>
                    <EnviosRfq envios={p.rfqEnvios} fmtD={fmtD} />

                    {/* Acciones de estado (mismas condiciones que la tabla desktop) */}
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:10 }}>
                      {p.status === "cotizando" && (
                        <ActionBtn variant="primary" onClick={() => setCargarCotizacionTarget(p)}
                          title="Cargar precios enviados por el proveedor">
                          ✓ Cargar cotización
                        </ActionBtn>
                      )}
                      {p.status === "cotizado" && (
                        <ActionBtn variant="next" onClick={() => handleConfirmarPedido(p._id)}
                          title="Confirmar pedido formal al proveedor">
                          → Confirmar pedido
                        </ActionBtn>
                      )}
                      {p.status === "pedido" && (
                        <ActionBtn variant="success" onClick={() => handleStatus(p._id,"enCamino")}
                          title="Marcar como despachada por el proveedor">
                          → En camino
                        </ActionBtn>
                      )}
                      {(p.status === "pedido" || p.status === "enCamino") && (
                        <ActionBtn variant="warning" onClick={() => openReceive(p._id)}
                          title="Registrar recepción + cargar factura y certificado">
                          ✓ Recibir
                        </ActionBtn>
                      )}
                      {(p.status === "cotizando" || p.status === "cotizado") && (
                        <ActionBtn variant="ghost" onClick={() => handleDuplicarOtroProveedor(p._id)}
                          title="Duplica esta cotización para pedir precios a otro proveedor.">
                          📋 Otro proveedor
                        </ActionBtn>
                      )}
                      {(p.status === "pedido" || p.status === "enCamino") && (
                        <ActionBtn variant="cancel" onClick={() => handleStatus(p._id,"cancelado")}
                          title="Cancelar (queda en historial como cancelada)">
                          ✕ Cancelar
                        </ActionBtn>
                      )}
                      <ActionBtn variant="danger" onClick={() => handleDelete(p)}
                        title="Eliminar definitivamente (no queda registro)">
                        🗑 Eliminar
                      </ActionBtn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        ) : (
        /* ══════════════ OC ACTIVAS ══════════════ */
        <div style={{ border:"1px solid #2e2e2e", borderRadius:8, overflow:"hidden", background:"#242424" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
            <thead>
              <tr>
                <th style={{ ...S.th, width:32 }} />
                <th style={{ ...S.th, width:110 }}>N° OC</th>
                <th style={{ ...S.th, width:180 }}>Proveedor</th>
                <th style={{ ...S.th }}>Ítems</th>
                <th style={{ ...S.th, width:110 }}>Total</th>
                <th style={{ ...S.th, width:110 }}>Estado</th>
                <th style={{ ...S.th, width:95  }}>ETA</th>
                <th style={{ ...S.th, width:280, textAlign:"right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivas.length === 0 ? (
                <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", color: "#999", padding:"24px 0" }}>
                  Sin órdenes activas
                </td></tr>
              ) : filteredActivas.map(p => {
                const isExp   = expandedId === p._id;
                const pName   = p.providerName || p.provider?.name || "—";
                const itemsDesc = (p.items || []).map(i => i.description).filter(Boolean).join(", ");
                return (
                  <React.Fragment key={p._id}>
                    <tr
                      onClick={() => setExpandedId(isExp ? null : p._id)}
                      style={{ cursor:"pointer" }}
                      onMouseEnter={hIn} onMouseLeave={hOut}
                    >
                      <td style={{ ...S.td, textAlign:"center", color: "#999" }}>
                        <span style={{ display:"inline-block", transition:"transform .15s", transform: isExp ? "rotate(90deg)" : "none" }}>›</span>
                      </td>
                      <td style={{ ...S.td, fontFamily:"monospace", color: "#bbb" }}>{padOC(p.number)}</td>
                      <td style={{ ...S.td, color:"#fff", fontWeight:600, overflow:"visible" }} onClick={e => e.stopPropagation()}>
                        {p.provider || p.providerName ? (
                          pName
                        ) : (
                          <div style={{ minWidth: 180 }}>
                            <EntityLookup
                              type="provider"
                              value={null}
                              onChange={v => handleAsignarProveedor(p._id, v)}
                              placeholder="Asignar proveedor…"
                            />
                          </div>
                        )}
                      </td>
                      <td style={{ ...S.td, whiteSpace:"normal" }}>
                        <span style={{ color: "#cbd5e1" }}>{(p.items||[]).length} ítem{(p.items||[]).length!==1?"s":""}</span>
                        {itemsDesc && <span style={{ color: "#999", fontSize:10 }}> · {itemsDesc.slice(0,60)}{itemsDesc.length>60?"…":""}</span>}
                        {respuestasProveedor[p._id] && (p.status === "cotizando" || p.status === "cotizado") && (
                          <AlertaRespuestaProveedor
                            respuesta={respuestasProveedor[p._id]}
                            onClick={e => { e.stopPropagation(); setCargarCotizacionTarget(p); }}
                          />
                        )}
                      </td>
                      <td style={{ ...S.td, color:"#2ecc71", fontWeight:600 }}>{fmtM(p.total)}</td>
                      <td style={{ ...S.td, overflow:"visible" }}><Badge status={p.status} /></td>
                      <td style={S.td}>{p.etaDate ? fmtD(p.etaDate) : "—"}</td>
                      <td style={{ ...S.td, overflow:"visible", padding:"6px 8px" }} onClick={e => e.stopPropagation()}>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"flex-end" }}>
                          {p.status === "cotizando" && (
                            <ActionBtn variant="primary" onClick={() => setCargarCotizacionTarget(p)}
                              title="Cargar precios enviados por el proveedor">
                              ✓ Cargar cotización
                            </ActionBtn>
                          )}
                          {p.status === "cotizado" && (
                            <ActionBtn variant="next" onClick={() => handleConfirmarPedido(p._id)}
                              title="Confirmar pedido formal al proveedor">
                              → Confirmar pedido
                            </ActionBtn>
                          )}
                          {p.status === "pedido" && (
                            <ActionBtn variant="success" onClick={() => handleStatus(p._id,"enCamino")}
                              title="Marcar como despachada por el proveedor">
                              → En camino
                            </ActionBtn>
                          )}
                          {(p.status === "pedido" || p.status === "enCamino") && (
                            <ActionBtn variant="warning" onClick={() => openReceive(p._id)}
                              title="Registrar recepción + cargar factura y certificado">
                              ✓ Recibir
                            </ActionBtn>
                          )}
                          {(p.status === "cotizando" || p.status === "cotizado") && (
                            <ActionBtn variant="ghost" onClick={() => handleDuplicarOtroProveedor(p._id)}
                              title="Duplica esta cotización para pedir precios a otro proveedor. El PDF que recibe cada proveedor no muestra ninguna referencia a la otra cotización.">
                              📋 Otro proveedor
                            </ActionBtn>
                          )}
                          {(p.status === "pedido" || p.status === "enCamino") && (
                            <ActionBtn variant="cancel" onClick={() => handleStatus(p._id,"cancelado")}
                              title="Cancelar (queda en historial como cancelada)">
                              ✕ Cancelar
                            </ActionBtn>
                          )}
                          <ActionBtn variant="danger" onClick={() => handleDelete(p)}
                            title="Eliminar definitivamente (no queda registro)">
                            🗑 Eliminar
                          </ActionBtn>
                        </div>
                      </td>
                    </tr>

                    {isExp && (
                      <tr>
                        <td colSpan={8} style={{ padding:0, borderBottom:"1px solid #2e2e2e" }}>
                          {/* clase expand-movil: en teléfono el detalle se pega al viewport
                              (sticky) en vez de quedar atrapado en el scroll de la tabla */}
                          <div className="expand-movil" style={{ background:"#1e1e1e", padding:"14px 20px" }}>
                            {/* Items detalle */}
                            <div style={{ fontSize:10, fontWeight:700, color: "#999", textTransform:"uppercase", letterSpacing:".04em", marginBottom:8 }}>
                              Ítems ({(p.items||[]).length})
                            </div>
                            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, marginBottom:14 }}>
                              <thead>
                                <tr>
                                  <th style={{ ...S.th, fontSize:9, padding:"4px 8px" }}>Descripción</th>
                                  <th style={{ ...S.th, fontSize:9, padding:"4px 8px", width:55 }}>Cant.</th>
                                  <th style={{ ...S.th, fontSize:9, padding:"4px 8px", width:50 }}>Unid.</th>
                                  <th style={{ ...S.th, fontSize:9, padding:"4px 8px", width:90 }}>P. Unit.</th>
                                  <th style={{ ...S.th, fontSize:9, padding:"4px 8px", width:90 }}>Subtotal</th>
                                  <th style={{ ...S.th, fontSize:9, padding:"4px 8px", width:120 }}>OT</th>
                                  <th style={{ ...S.th, fontSize:9, padding:"4px 8px", width:80 }}>Cert.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(p.items||[]).map((item, idx) => (
                                  <tr key={item._id || idx}>
                                    <td style={{ ...S.td, fontSize:11, padding:"5px 8px" }}>
                                      {item.description}
                                      {item.notes && <div style={{ fontSize:10, color:"#8a8a8a" }}>{item.notes}</div>}
                                    </td>
                                    <td style={{ ...S.td, fontSize:11, padding:"5px 8px" }}>{item.quantity} {item.unit}</td>
                                    <td style={{ ...S.td, fontSize:11, padding:"5px 8px" }}>{item.unit}</td>
                                    <td style={{ ...S.td, fontSize:11, padding:"5px 8px" }}>{item.pUnit ? fmtM(item.pUnit) : "—"}</td>
                                    <td style={{ ...S.td, fontSize:11, padding:"5px 8px", color:"#2ecc71" }}>{item.subtotal ? fmtM(item.subtotal) : "—"}</td>
                                    <td style={{ ...S.td, fontSize:11, padding:"5px 8px", fontFamily:"monospace", color:"#f39c12" }}>
                                      {item.workOrderRef ? (typeof item.workOrderRef === "object" ? padOT(item.workOrderRef.number) : "—") : "—"}
                                    </td>
                                    <td style={{ ...S.td, fontSize:11, padding:"5px 8px", textAlign:"center" }}>
                                      {item.requiereCertificado
                                        ? <span title="Solicitar certificado al proveedor" style={{ color:"#f39c12" }}>⚠ requiere</span>
                                        : <span style={{ color: "#999" }}>—</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            <ExpedienteCompra po={p} onCambio={load} />

                            {/* F5.9 / D-T26 — Panel de certificados de material */}
                            {(p.items||[]).some(it => it.requiereCertificado) && (
                              <CertificadoPanel purchaseOrderId={p._id} />
                            )}

                            {/* Notas y acciones */}
                            <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
                              {p.internalNotes && (
                                <div style={{ flex:1, minWidth:160 }}>
                                  <div style={{ fontSize:10, color: "#999", marginBottom:3, textTransform:"uppercase", letterSpacing:".04em" }}>Nota interna</div>
                                  <div style={{ fontSize:11, color: "#cbd5e1" }}>{p.internalNotes}</div>
                                </div>
                              )}
                              {p.externalNotes && (
                                <div style={{ flex:1, minWidth:160 }}>
                                  <div style={{ fontSize:10, color: "#999", marginBottom:3, textTransform:"uppercase", letterSpacing:".04em" }}>Nota al proveedor</div>
                                  <div style={{ fontSize:11, color: "#cbd5e1" }}>{p.externalNotes}</div>
                                </div>
                              )}
                              <div style={{ flexShrink:0, display:"flex", alignItems:"flex-end", gap:8 }}>
                                <button
                                  onClick={() => handleVerPdfOficial(p)}
                                  disabled={pdfOficialLoadingId === p._id}
                                  title="PDF generado por el servidor — el mismo documento que Heimdall envía al proveedor"
                                  style={{ background:"transparent", color:"#2ecc71", border:"1px solid #14532d", borderRadius:5, padding:"5px 12px", fontSize:11, cursor:"pointer", opacity: pdfOficialLoadingId === p._id ? 0.6 : 1 }}
                                >
                                  {pdfOficialLoadingId === p._id ? "Generando…" : `↓ PDF ${p.status === "cotizando" || p.status === "cotizado" ? "solicitud" : "OC"}`}
                                </button>
                                <button
                                  onClick={() => handleVerPdfOficial(p, "descargar")}
                                  disabled={pdfOficialLoadingId === p._id}
                                  title="Guardar el PDF con su nombre (SolicitudCotizacion-N.pdf)"
                                  style={{ background:"transparent", color:"#8a8a8a", border:"1px solid #2e2e2e", borderRadius:5, padding:"5px 10px", fontSize:11, cursor:"pointer" }}
                                >
                                  💾 Guardar
                                </button>
                                {(p.status === "cotizando" || p.status === "pedido") && (
                                  <button
                                    onClick={() => abrirEnviarRfq(p)}
                                    title={`Enviar la ${p.status === "pedido" ? "orden de compra" : "solicitud"} con el PDF adjunto desde recepcion@binimeliz.com`}
                                    style={{ background:"#0d1a2d", color:"#3498db", border:"1px solid #0d2a4a", borderRadius:5, padding:"5px 12px", fontSize:11, cursor:"pointer" }}
                                  >
                                    ✉ Enviar {p.status === "pedido" ? "OC" : ""} al proveedor
                                  </button>
                                )}
                              </div>
                            </div>
                            <EnviosRfq envios={p.rfqEnvios} fmtD={fmtD} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        )

      ) : tab === "alertas" ? (
        <>
        {/* ══════ AVISOS DE COMPRA POR FABRICACIÓN (T1, 26-ago) ══════
            Nacen cuando en /produccion se decide Fabricar una pieza y el
            material que consume no alcanza. A diferencia de las alertas de
            stock de abajo, estas tienen una OT detrás y son urgentes: la pieza
            YA se mandó a fabricar. */}
        {avisos.length > 0 && (
          <div style={{ border:"1px solid #4a2a00", borderRadius:8, overflow:"hidden", background:"#242424", marginBottom:14 }}>
            <div style={{ padding:"8px 12px", background:"#2a1a00", borderBottom:"1px solid #4a2a00", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:12, fontWeight:700, color:"#f39c12" }}>
                Material por comprar
              </span>
              <span style={{
                display:"inline-block", fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20,
                background:"#4a2a00", color:"#f39c12", border:"1px solid #f39c1240",
              }}>{avisos.length}</span>
              <span style={{ fontSize:10, color:"#777" }}>
                producción pidió material o se mandó a fabricar sin stock: el primer paso es pedir cotización
              </span>
              {/* 13-oct: marcar varios y pedir precio de una vez (una solicitud por proveedor). */}
              <button
                onClick={abrirCotizarMarcados}
                disabled={avisosMarcados.length === 0}
                title={avisosMarcados.length ? "Pedir cotización de los avisos marcados" : "Marque uno o más avisos"}
                style={{ ...BTN_AVISO.oc, ...(isMobile ? BTN_AVISO_MOVIL : { fontSize:11, padding:"4px 10px", fontWeight:600 }),
                         marginLeft:"auto", opacity: avisosMarcados.length ? 1 : 0.5, cursor: avisosMarcados.length ? "pointer" : "default" }}
              >
                Cotizar seleccionados ({avisosMarcados.length})
              </button>
            </div>
            {isMobile ? (
            /* Celular (13-oct): la tabla de 8 columnas dejaba Pieza y Material en
               0 px y los botones fuera de pantalla. Cada aviso va como tarjeta. */
            <div style={{ display:"flex", flexDirection:"column", gap:8, padding:10 }}>
              {avisos.map((a, i) => {
                const materialAviso = materialDeAviso(a);
                const porDefinir = !materialAviso;
                return (
                  <div key={a._id || i} style={{ ...CARD_MOVIL, borderColor: avisosMarcados.includes(a._id) ? "var(--tab-ambar)" : "var(--tab-borde)" }}>
                    <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer" }}>
                      <input
                        type="checkbox" checked={avisosMarcados.includes(a._id)} onChange={() => alternarAviso(a._id)}
                        aria-label="Marcar para cotizar"
                        style={{ width:22, height:22, marginTop:1, accentColor:"var(--tab-ambar)", flexShrink:0 }}
                      />
                      <span style={{ fontSize:14, fontWeight:600, color:"var(--tab-texto)", overflowWrap:"anywhere" }}>
                        {a.piezaNombre || "—"}
                        {a.cantidadPiezas ? <span style={{ color:"var(--tab-suave)", fontWeight:400 }}> × {a.cantidadPiezas}</span> : null}
                      </span>
                    </label>
                    {a.piezaSku && (
                      <div style={{ fontFamily:"monospace", fontSize:11, color:"var(--tab-azul)", marginTop:2 }}>{a.piezaSku}</div>
                    )}
                    <div style={{ fontSize:12, marginTop:8, overflowWrap:"anywhere", color: porDefinir ? "var(--tab-ambar)" : undefined }}>
                      {materialAviso || MATERIAL_POR_DEFINIR}
                    </div>
                    <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8, marginTop:6, flexWrap:"wrap" }}>
                      <span style={{ fontSize:13, fontWeight:700, color:"var(--tab-rojo)", fontVariantNumeric:"tabular-nums" }}>
                        Falta {a.faltante} {a.unidad}
                      </span>
                      <span style={{ fontSize:11, fontFamily:"monospace", color:"var(--tab-suave)" }}>
                        {a.workOrder?.number ? `OT #${a.workOrder.number}` : "Sin OT"}
                      </span>
                    </div>
                    <div style={{ fontSize:11, color:"var(--tab-suave)", marginTop:2, fontVariantNumeric:"tabular-nums" }}>
                      Requerido {a.requerido} {a.unidad} · disponible {a.disponible} {a.unidad}
                    </div>
                    <div style={{ display:"flex", gap:10, marginTop:10 }}>
                      <button
                        onClick={() => setCotizarAvisos([a])}
                        style={{ ...BTN_AVISO.oc, ...BTN_AVISO_MOVIL, flex:1 }}
                      >
                        + Cotizar
                      </button>
                      <button
                        onClick={() => handleAtenderAviso(a._id)}
                        aria-label="Marcar como atendido"
                        style={{ ...BTN_AVISO.ok, ...BTN_AVISO_MOVIL }}
                      >
                        ✓ Atendido
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            ) : (
            /* Escritorio: si la ventana es angosta la tabla se desplaza de lado
               en vez de cortar columnas. */
            <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", minWidth:854, borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width:34 }}>
                    <input
                      type="checkbox" aria-label="Marcar todos"
                      checked={avisos.length > 0 && avisos.every(a => avisosMarcados.includes(a._id))}
                      onChange={e => setAvisosMarcados(e.target.checked ? avisos.map(a => a._id) : [])}
                      style={{ accentColor:"var(--tab-ambar)", cursor:"pointer" }}
                    />
                  </th>
                  <th style={{ ...S.th }}>Pieza</th>
                  <th style={{ ...S.th, width:110 }}>SKU</th>
                  <th style={{ ...S.th }}>Material</th>
                  <th style={{ ...S.th, width:80 }}>Requerido</th>
                  <th style={{ ...S.th, width:80 }}>Disponible</th>
                  <th style={{ ...S.th, width:80 }}>Falta</th>
                  <th style={{ ...S.th, width:90 }}>OT</th>
                  <th style={{ ...S.th, width:110 }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {avisos.map((a, i) => (
                  <tr key={a._id || i} onMouseEnter={hIn} onMouseLeave={hOut}>
                    <td style={{ ...S.td, overflow:"visible" }}>
                      <input
                        type="checkbox" checked={avisosMarcados.includes(a._id)} onChange={() => alternarAviso(a._id)}
                        aria-label={`Marcar ${a.piezaNombre || "aviso"} para cotizar`}
                        style={{ accentColor:"var(--tab-ambar)", cursor:"pointer" }}
                      />
                    </td>
                    <td style={{ ...S.td, color:"#fff", fontWeight:600 }}>
                      {a.piezaNombre || "—"}
                      {a.cantidadPiezas ? <span style={{ color:"#777", fontWeight:400 }}> × {a.cantidadPiezas}</span> : null}
                    </td>
                    <td style={{ ...S.td, fontFamily:"monospace", color:"#3498db" }}>{a.piezaSku || "—"}</td>
                    <td style={{ ...S.td, color: materialDeAviso(a) ? "#ddd" : "var(--tab-ambar)" }} title={a.nota || ""}>{materialDeAviso(a) || MATERIAL_POR_DEFINIR}</td>
                    <td style={{ ...S.td, fontVariantNumeric:"tabular-nums" }}>{a.requerido} {a.unidad}</td>
                    <td style={{ ...S.td, fontVariantNumeric:"tabular-nums", color: a.disponible > 0 ? "#bbb" : "#e74c3c" }}>
                      {a.disponible} {a.unidad}
                    </td>
                    <td style={{ ...S.td, color:"#e74c3c", fontWeight:700, fontVariantNumeric:"tabular-nums" }}>
                      {a.faltante} {a.unidad}
                    </td>
                    <td style={{ ...S.td, fontFamily:"monospace", color:"#999" }}>
                      {a.workOrder?.number ? `#${a.workOrder.number}` : "—"}
                    </td>
                    <td style={{ ...S.td, overflow:"visible", whiteSpace:"nowrap" }}>
                      <button
                        onClick={() => setCotizarAvisos([a])}
                        title={`Pedir cotización por ${a.faltante} ${a.unidad} de ${materialDeAviso(a) || "material por definir"}`}
                        style={{ ...BTN_AVISO.oc, fontSize:10, padding:"3px 8px", marginRight:5 }}
                      >
                        + Cotizar
                      </button>
                      <button
                        onClick={() => handleAtenderAviso(a._id)}
                        title="Marcar como atendido — el aviso queda registrado, no se borra"
                        style={{ ...BTN_AVISO.ok, fontSize:10, padding:"3px 8px" }}
                      >
                        ✓
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            )}
          </div>
        )}

        {/* ══════ AVISOS EN COTIZACIÓN (13-oct) ══════
            Solo lectura: ya se pidió precio. Pasan a atendidos solos cuando una de sus
            solicitudes se confirma como OC, y vuelven arriba si todas se cancelan. */}
        {avisosCotizando.length > 0 && (
          <div style={{ border:"1px solid var(--tab-borde)", borderRadius:8, background:"var(--tab-caja)", marginBottom:14, padding:"8px 12px" }}>
            <div style={{ fontSize:12, fontWeight:700, color:"var(--tab-azul)", marginBottom:6 }}>
              En cotización ({avisosCotizando.length})
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {avisosCotizando.map(a => (
                <div key={a._id} style={{ display:"flex", flexWrap:"wrap", alignItems:"baseline", gap:"4px 10px", fontSize:12, paddingTop:6, borderTop:"1px solid var(--tab-borde)" }}>
                  <span style={{ color:"var(--tab-texto)", fontWeight:600, overflowWrap:"anywhere" }}>{a.piezaNombre || "—"}</span>
                  <span style={{ color:"var(--tab-suave)", overflowWrap:"anywhere" }}>
                    {materialDeAviso(a) || MATERIAL_POR_DEFINIR}{a.workOrder?.number ? ` · OT #${a.workOrder.number}` : ""}
                  </span>
                  <span style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                    {(a.solicitudes || []).filter(s => s && s._id).map(s => (
                      <button
                        key={s._id}
                        onClick={() => { setFilter(String(s.number || "")); setExpandedId(s._id); setTab("activas"); }}
                        title="Ver la solicitud en Activas"
                        style={{ ...BTN_BASE, background:"transparent", color:"var(--tab-azul)", border:"1px solid var(--tab-borde)",
                                 ...(isMobile ? { minHeight:36 } : {}) }}
                      >
                        N°{s.number}{s.providerName ? ` · ${s.providerName}` : ""}{s.status === "cancelado" ? " (descartada)" : ""}
                      </button>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════ ALERTAS DE STOCK ══════════════ */}
        {isMobile ? (
        /* Celular (13-oct): tarjetas, igual que los avisos de arriba. */
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {alertas.length === 0 ? (
            <div style={{ ...CARD_MOVIL, textAlign:"center", color:"var(--tab-verde)", padding:"24px 0" }}>
              ✓ Sin alertas de stock
            </div>
          ) : alertas.map((p, i) => (
            <div key={p._id || i} style={CARD_MOVIL}>
              <div style={{ fontSize:14, fontWeight:600, color:"var(--tab-texto)", overflowWrap:"anywhere" }}>{p.name || "—"}</div>
              {p.sku && (
                <div style={{ fontFamily:"monospace", fontSize:11, color:"var(--tab-azul)", marginTop:2 }}>{p.sku}</div>
              )}
              <div style={{ fontSize:12, marginTop:8, fontVariantNumeric:"tabular-nums" }}>
                Stock <b style={{ color:"var(--tab-rojo)" }}>{p.stock ?? "—"}</b>
                {" · "}reorder point <b style={{ color:"var(--tab-ambar)" }}>{p.reorderPoint ?? "—"}</b>
                {p.stock != null && p.reorderPoint != null && (
                  <> · déficit <b style={{ color:"var(--tab-rojo)" }}>{p.reorderPoint - p.stock}</b></>
                )}
              </div>
              <button
                onClick={() => { openForm(p); setTab("activas"); }}
                style={{ ...BTN_AVISO.oc, ...BTN_AVISO_MOVIL, width:"100%", marginTop:10 }}
              >
                + Cotizar
              </button>
            </div>
          ))}
        </div>
        ) : (
        <div style={{ border:"1px solid #2e2e2e", borderRadius:8, overflowX:"auto", background:"#242424" }}>
          <table style={{ width:"100%", minWidth:680, borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
            <thead>
              <tr>
                <th style={{ ...S.th }}>Pieza</th>
                <th style={{ ...S.th, width:110 }}>SKU</th>
                <th style={{ ...S.th, width:90 }}>Stock actual</th>
                <th style={{ ...S.th, width:90 }}>Reorder point</th>
                <th style={{ ...S.th, width:80 }}>Déficit</th>
                <th style={{ ...S.th, width:120 }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {alertas.length === 0 ? (
                <tr><td colSpan={6} style={{ ...S.td, textAlign:"center", color:"#2ecc71", padding:"24px 0" }}>
                  ✓ Sin alertas de stock
                </td></tr>
              ) : alertas.map((p, i) => (
                <tr key={p._id || i} onMouseEnter={hIn} onMouseLeave={hOut}>
                  <td style={{ ...S.td, color:"#fff", fontWeight:600 }}>{p.name || "—"}</td>
                  <td style={{ ...S.td, fontFamily:"monospace", color:"#3498db" }}>{p.sku || "—"}</td>
                  <td style={{ ...S.td, color:"#e74c3c", fontWeight:700 }}>{p.stock ?? "—"}</td>
                  <td style={{ ...S.td, color:"#f39c12" }}>{p.reorderPoint ?? "—"}</td>
                  <td style={{ ...S.td, color:"#e74c3c" }}>
                    {p.stock != null && p.reorderPoint != null ? p.reorderPoint - p.stock : "—"}
                  </td>
                  <td style={{ ...S.td, overflow:"visible" }}>
                    <button
                      onClick={() => { openForm(p); setTab("activas"); }}
                      style={{ ...BTN_AVISO.oc, fontSize:10, padding:"3px 10px" }}
                    >
                      + Cotizar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>

      ) : tab === "historial" ? (
        /* ══════════════ HISTORIAL ══════════════ */
        <HistorialPanel
          historial={historial}
          historialMonth={historialMonth}
          setHistorialMonth={setHistorialMonth}
          fmtM={fmtM}
          fmtD={fmtD}
          padOC={padOC}
          hIn={hIn}
          hOut={hOut}
          onVerPdf={handleVerPdfOficial}
          pdfLoadingId={pdfOficialLoadingId}
        />
      ) : (
        /* ══════════════ SIN REGISTRO (RCV↔OC) ══════════════ */
        <SinRegistroPanel fmtM={fmtM} fmtD={fmtD} hIn={hIn} hOut={hOut} />
      )}

      {/* ── Previsualización OC (PDF) ── */}
      {pdfData && (
        <PurchaseOrderPDF
          order={pdfData.order}
          provider={pdfData.provider}
          onClose={() => setPdfData(null)}
        />
      )}

      {/* ── Hoja: solicitud de cotización desde avisos de material (13-oct) ── */}
      {cotizarAvisos && (
        <CotizarAvisos
          avisos={cotizarAvisos}
          centrada={!isMobile}
          onCerrar={() => setCotizarAvisos(null)}
          onListo={handleCotizacionGenerada}
          onRecargar={load}
        />
      )}

      {/* ── Modal: enviar solicitud de cotización al proveedor ── */}
      {enviarRfq && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#242424", border:"1px solid #2e2e2e", borderRadius:10, padding:"20px 24px", width:"min(480px, 94vw)", maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:4 }}>
              Enviar {enviarRfq.po.status === "pedido" ? "orden de compra" : "solicitud de cotización"} N°{enviarRfq.po.number}
            </div>
            <div style={{ fontSize:11, color:"#cbd5e1", marginBottom:14 }}>
              Sale desde <strong>recepcion@binimeliz.com</strong> con el PDF adjunto
              ({enviarRfq.po.providerName || enviarRfq.po.provider?.name || "sin proveedor asignado"}).
              El proveedor responde a esa misma casilla.
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:14 }}>
              <div>
                <label style={S.lbl}>Para (correo del proveedor) *</label>
                <input
                  type="email" value={enviarRfq.para} autoFocus
                  onChange={e => setEnviarRfq(f => ({ ...f, para: e.target.value }))}
                  placeholder="ventas@proveedor.cl" style={S.input}
                />
                {enviarRfq.preview && !enviarRfq.preview.para && (
                  <div style={{ fontSize:10, color:"#f39c12", marginTop:4 }}>
                    El proveedor no tiene correo guardado en Contactos; el que escriba acá se usa solo para este envío.
                  </div>
                )}
              </div>
              {enviarRfq.preview && (
                <div>
                  <label style={S.lbl}>Así sale el correo</label>
                  <div style={{ background:"#161616", border:"1px solid #333", borderRadius:4, padding:"8px 10px", fontSize:11, color:"#bbb" }}>
                    <div style={{ marginBottom:6 }}><span style={{ color:"#8a8a8a" }}>Asunto:</span> {enviarRfq.preview.asunto}</div>
                    <div style={{ marginBottom:6 }}><span style={{ color:"#8a8a8a" }}>Adjunto:</span> {enviarRfq.preview.adjunto}</div>
                    <pre style={{ margin:0, whiteSpace:"pre-wrap", fontFamily:"inherit", color:"#ddd" }}>
                      {enviarRfq.mensaje
                        ? enviarRfq.preview.cuerpo.replace(/\n\nLugar de entrega:/, `\n\n${enviarRfq.mensaje.trim()}\n\nLugar de entrega:`)
                        : enviarRfq.preview.cuerpo}
                    </pre>
                  </div>
                </div>
              )}
              <div>
                <label style={S.lbl}>Mensaje adicional (opcional)</label>
                <textarea
                  value={enviarRfq.mensaje} rows={3}
                  onChange={e => setEnviarRfq(f => ({ ...f, mensaje: e.target.value }))}
                  placeholder="Ej.: Favor indicar plazo de entrega y equivalente comercial si no hay la medida exacta."
                  style={{ ...S.input, resize:"vertical" }}
                />
              </div>
              {enviarRfq.error && (
                <div style={{ fontSize:11, color:"#e74c3c", background:"#2a0d0d", border:"1px solid #4a0000", borderRadius:5, padding:"6px 10px" }}>
                  {enviarRfq.error}
                  {enviarRfq.codigo === "SIN_SALIDA_SMTP" && enviarRfq.respaldo && (
                    <div style={{ marginTop:8, color:"#cbd5e1" }}>
                      Mientras el hosting no habilite la salida de correo, puede mandarlo desde su propio correo con el mismo texto:
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:6 }}>
                        <button type="button" onClick={() => handleVerPdfOficial(enviarRfq.po, "descargar")}
                          style={{ ...BTN_BASE, ...BTN_VARIANTS.ghost }}>💾 1. Descargar {enviarRfq.respaldo.adjunto}</button>
                        <a href={`mailto:${encodeURIComponent(enviarRfq.respaldo.para)}?subject=${encodeURIComponent(enviarRfq.respaldo.asunto)}&body=${encodeURIComponent(enviarRfq.respaldo.cuerpo)}`}
                          style={{ ...BTN_BASE, ...BTN_VARIANTS.next, textDecoration:"none", display:"inline-block" }}>✉ 2. Abrir en mi correo y adjuntarlo</a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
              <button onClick={() => setEnviarRfq(null)} disabled={enviarRfq.enviando}
                style={{ ...BTN_BASE, ...BTN_VARIANTS.cancel }}>Cancelar</button>
              <button onClick={handleEnviarRfq} disabled={enviarRfq.enviando}
                style={{ ...BTN_BASE, ...BTN_VARIANTS.next, opacity: enviarRfq.enviando ? 0.6 : 1 }}>
                {enviarRfq.enviando ? "Enviando…" : "✉ Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Marcar recibida + cargar factura proveedor ── */}
      {receiveId && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#242424", border:"1px solid #2e2e2e", borderRadius:10, padding:"20px 24px", width:"min(480px, 94vw)", maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:4 }}>Registrar recepción + factura</div>
            <div style={{ fontSize:11, color: "#cbd5e1", marginBottom:14 }}>
              Cierra el ciclo de la compra. Cargar factura aquí queda compatible con
              la conciliación automática de SII (RCV compras) cuando se active.
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:14 }}>
              <div>
                <label style={S.lbl}>N° Factura proveedor *</label>
                <input value={receiveForm.invoiceNumber} onChange={e => setReceiveForm(f => ({...f, invoiceNumber:e.target.value}))} placeholder="Ej: 123456" style={S.input} />
              </div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" /* móvil */ }}>
                <div style={{ flex:1, minWidth:130 }}>
                  <label style={S.lbl}>Monto total factura</label>
                  <input type="number" value={receiveForm.invoiceAmount} onChange={e => setReceiveForm(f => ({...f, invoiceAmount:e.target.value}))} placeholder="0" style={S.input} />
                </div>
                <div style={{ flex:1, minWidth:130 }}>
                  <label style={S.lbl}>Fecha recepción</label>
                  <input type="date" value={receiveForm.receivedDate} onChange={e => setReceiveForm(f => ({...f, receivedDate:e.target.value}))} style={S.input} />
                </div>
              </div>
              <div>
                <label style={S.lbl}>📎 PDF de la factura del proveedor (opcional pero recomendado)</label>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <label style={{
                    flex:1, padding:"7px 10px",
                    background:"#161616", border:"1px dashed #444",
                    borderRadius:5, color: receiveForm.invoiceFile ? "#2ecc71" : "#888",
                    fontSize:11, cursor:"pointer", textAlign:"center",
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  }}>
                    <input type="file" accept="application/pdf,image/*"
                      onChange={e => setReceiveForm(f => ({ ...f, invoiceFile: e.target.files?.[0] || null }))}
                      style={{ display:"none" }}
                    />
                    {receiveForm.invoiceFile ? `✓ ${receiveForm.invoiceFile.name}` : "Seleccionar PDF de factura"}
                  </label>
                  {receiveForm.invoiceFile && (
                    <button type="button" onClick={() => setReceiveForm(f => ({ ...f, invoiceFile: null }))}
                      style={{ background:"transparent", color: "#cbd5e1", border:"1px solid #444", borderRadius:5, padding:"5px 9px", fontSize:11, cursor:"pointer" }}
                    >×</button>
                  )}
                </div>
              </div>
              <div>
                <label style={S.lbl}>📎 Certificado de material/calidad (siempre pedirlo al proveedor)</label>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <label style={{
                    flex:1, padding:"7px 10px",
                    background:"#161616", border:"1px dashed #f39c12",
                    borderRadius:5, color: receiveForm.certificateFile ? "#2ecc71" : "#f39c12",
                    fontSize:11, cursor:"pointer", textAlign:"center",
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  }}>
                    <input type="file" accept="application/pdf,image/*"
                      onChange={e => setReceiveForm(f => ({ ...f, certificateFile: e.target.files?.[0] || null }))}
                      style={{ display:"none" }}
                    />
                    {receiveForm.certificateFile ? `✓ ${receiveForm.certificateFile.name}` : "Seleccionar certificado (PDF, foto, scan)"}
                  </label>
                  {receiveForm.certificateFile && (
                    <button type="button" onClick={() => setReceiveForm(f => ({ ...f, certificateFile: null }))}
                      style={{ background:"transparent", color: "#cbd5e1", border:"1px solid #444", borderRadius:5, padding:"5px 9px", fontSize:11, cursor:"pointer" }}
                    >×</button>
                  )}
                </div>
                <div style={{ fontSize:9, color: "#aaa", marginTop:3 }}>
                  Los proveedores los entregan solo cuando se les pide. Alimenta el dossier de calidad de las OTs que usan este material.
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={() => setReceiveId(null)} style={{ background:"transparent", color: "#cbd5e1", border:"1px solid #444", borderRadius:6, padding:"7px 14px", fontSize:12, cursor:"pointer" }}>Cancelar</button>
              <button onClick={handleReceive} style={{ background:"#2ecc71", color:"#111", border:"none", borderRadius:6, padding:"7px 18px", fontSize:12, fontWeight:700, cursor:"pointer" }}>✓ Marcar recibida</button>
            </div>
          </div>
        </div>
      )}

      {/* D-Compras-Fase2 — Modal cargar cotización del proveedor */}
      <CargarCotizacionModal
        po={cargarCotizacionTarget}
        onClose={() => setCargarCotizacionTarget(null)}
        onAdjudicado={() => { setCargarCotizacionTarget(null); load(); }}
        onSaved={updated => {
          setActivas(prev => prev.map(p => p._id === updated._id ? updated : p));
          setCargarCotizacionTarget(null);
        }}
      />
    </div>
  );
};

// D-Compras-Fase2 — Modal para cargar precios cuando llega la cotización del
// proveedor. Permite seleccionar QUÉ items se compran (no necesariamente todos),
// adjuntar el PDF de cotización del proveedor, y elegir entre solo guardar la
// cotización (status=cotizado) o convertir directo en OC (status=pedido).
// ── Cargar / leer cotización del proveedor ─────────────────────────────────
// 30-sep-2026 (Maxi): la app LEE la cotización que respondió el proveedor (PDF o foto)
// y la cruza con la solicitud, línea por línea. La vista muestra qué pedimos, qué
// cotizaron, el precio por trozo y las VARIACIONES (diámetro, largo, cantidad, material,
// faltante) para que una persona apruebe y de ahí salga la OC. La carga manual de precios
// sigue disponible si no hay archivo. Nada se aprueba solo.
const NIVEL_COLOR = { verde: "#2ecc71", amarillo: "#f39c12", rojo: "#e74c3c" };
const fmtCLP0 = n => `$${Math.round(Number(n) || 0).toLocaleString("es-CL")}`;

const CargarCotizacionModal = ({ po, onClose, onSaved, onAdjudicado }) => {
  const [items, setItems] = useState([]);
  const [propuesta, setPropuesta] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorLectura, setErrorLectura] = useState(null);
  const [correos, setCorreos] = useState([]);       // cotizaciones recibidas en recepcion@
  const [leyendoMail, setLeyendoMail] = useState(null);
  // 02-oct-2026 — la misma lista cotizada a otros proveedores (vista "Comparar proveedores").
  const [comparacion, setComparacion] = useState(null);
  const [vista, setVista] = useState("una");

  // Ítems a partir de la propuesta leída (o de la PO pelada si no hay propuesta).
  const itemsDesde = (poBase, prop) => {
    const porId = new Map((prop?.lineas || []).map(l => [String(l.itemId), l]));
    return (poBase.items || []).map(it => {
      const l = porId.get(String(it._id));
      return {
        _id: it._id,
        descripcionOriginal: it.description,
        description: l?.propuesta?.description || it.description,
        quantity: l?.propuesta?.quantity || it.quantity,
        unit: it.unit,
        notes: it.notes || "",
        terminada: l?.solicitado?.terminada || "",
        pUnit: l ? (l.propuesta?.pUnit || 0) : (it.pUnit || 0),
        selected: l ? !!l.propuesta?.aprobar : true,
        cotizado: l?.cotizado || null,
        variaciones: l?.variaciones || [],
        nivel: l?.nivel || null,
      };
    });
  };

  useEffect(() => {
    if (!po) return;
    // Si ya se leyó una cotización y no se aprobó, se retoma esa propuesta.
    const prev = po.cotizacionProveedor && !po.cotizacionProveedor.aprobadaEn ? po.cotizacionProveedor : null;
    setPropuesta(prev);
    setItems(itemsDesde(po, prev));
    setPdfFile(null);
    setErrorLectura(null);
    setCorreos([]);
    backend.get(`purchaseOrder/${po._id}/cotizaciones-en-correo`)
      .then(r => setCorreos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCorreos([]));
    setVista("una");
  }, [po?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Se vuelve a pedir cuando cambia la propuesta leída: una lectura nueva puede sumar un proveedor.
  useEffect(() => {
    if (!po) return;
    setComparacion(null);
    backend.get(`purchaseOrder/${po._id}/comparar-proveedores`)
      .then(r => setComparacion(r.data))
      .catch(() => setComparacion(null));
  }, [po?._id, propuesta?.leidaEn]); // eslint-disable-line react-hooks/exhaustive-deps
  const puedeComparar = (comparacion?.proveedores || []).length >= 2;
  const comparando = puedeComparar && vista === "comparar";

  if (!po) return null;

  const selectedItems = items.filter(it => it.selected);
  const total = selectedItems.reduce((s, it) => s + (Number(it.pUnit) || 0) * (Number(it.quantity) || 0), 0);
  const noSeleccionados = items.filter(it => !it.selected);

  // Sube el archivo del proveedor vinculado a la PO (respaldo de dónde salió cada precio).
  const subirArchivo = async (archivo, etiqueta) => {
    if (!archivo) return;
    const fd = new FormData();
    fd.append("file", archivo);
    fd.append("docType", "cotizacionProveedor");
    fd.append("purchaseOrderRefs", po._id);
    fd.append("label", etiqueta);
    if (po.provider?._id) fd.append("cotProveedor", po.provider._id);
    try {
      await backend.post("document/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) {
      // Con la lista blanca por bytes (21-oct) un archivo que no es PDF/imagen da 415: hay que decirlo.
      console.warn("[upload cotización proveedor]", e);
      alert("Los precios se guardaron, pero el archivo no quedó en el expediente: " + (e?.response?.data?.error || e.message));
    }
  };

  // Lee el PDF/foto en el servidor y muestra la propuesta cruzada.
  const handleLeer = async () => {
    if (!pdfFile) { alert("Seleccione primero el PDF o la foto de la cotización."); return; }
    setLeyendo(true); setErrorLectura(null);
    try {
      const fd = new FormData();
      fd.append("file", pdfFile);
      const res = await backend.post(`purchaseOrder/${po._id}/leer-cotizacion`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const prop = res.data.propuesta;
      setPropuesta(prop);
      setItems(itemsDesde(po, prop));
      // 21-oct: el servidor ya guardó el archivo en el expediente (leer-cotizacion); subirlo de
      // nuevo acá lo duplicaba (así quedaron dos copias en la N°17).
      setPdfFile(null);
    } catch (e) {
      setErrorLectura(e?.response?.data?.error || e.message);
    }
    setLeyendo(false);
  };

  // Lee la cotización directo del correo recibido en recepcion@ (sin descargar ni subir).
  const handleLeerCorreo = async (mail, adjunto) => {
    setLeyendoMail(mail._id); setErrorLectura(null);
    try {
      const res = await backend.post(`purchaseOrder/${po._id}/leer-cotizacion-correo`, { mailId: mail._id, adjunto: adjunto?.nombre || null });
      const prop = res.data.propuesta;
      setPropuesta(prop);
      setItems(itemsDesde(po, prop));
    } catch (e) {
      setErrorLectura(e?.response?.data?.error || e.message);
    }
    setLeyendoMail(null);
  };

  const lineasDecision = () => items.map(it => ({
    itemId: it._id, aprobar: !!it.selected, pUnit: Number(it.pUnit) || 0,
    description: it.description, quantity: Number(it.quantity) || undefined,
    notaVariacion: it.variaciones?.filter(v => v.tipo !== "faltante").map(v => v.texto).join(" ") || undefined,
  }));

  // Con propuesta: aprobar en el servidor (queda cotizado). Sin propuesta: carga manual clásica.
  const handleGuardar = async () => {
    setSaving(true);
    try {
      let actualizado;
      if (propuesta) {
        const res = await backend.patch(`purchaseOrder/${po._id}/aprobar-cotizacion`, { lineas: lineasDecision(), generarOC: false });
        actualizado = res.data.po;
      } else {
        const res = await backend.patch(`purchaseOrder/${po._id}/cargar-cotizacion`, {
          items: items.map(it => ({ _id: it._id, pUnit: Number(it.pUnit) || 0 })),
        });
        await subirArchivo(pdfFile, `Cotización proveedor ${po.provider?.name || po.providerName || ""}`);
        actualizado = res.data;
      }
      onSaved?.(actualizado);
    } catch (e) {
      alert("Error: " + (e?.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  // Genera la OC con lo aprobado; lo no aprobado se puede pedir a otro proveedor.
  const handleGenerarOC = async () => {
    if (selectedItems.length === 0) { alert("Marque al menos un ítem para comprar."); return; }
    const sinPrecio = selectedItems.filter(it => !(Number(it.pUnit) > 0));
    if (sinPrecio.length) { alert(`Hay ${sinPrecio.length} ítem(s) aprobados sin precio.`); return; }
    if (!window.confirm(
      `¿Generar la orden de compra? Se compran ${selectedItems.length} de ${items.length} ítems por ${fmtCLP0(total)} neto` +
      (noSeleccionados.length ? `. Los ${noSeleccionados.length} no aprobados quedan fuera de esta OC.` : ".")
    )) return;
    setSaving(true);
    try {
      let actualizado, noAprobados = [];
      if (propuesta) {
        const res = await backend.patch(`purchaseOrder/${po._id}/aprobar-cotizacion`, { lineas: lineasDecision(), generarOC: true });
        actualizado = res.data.po; noAprobados = res.data.noAprobados || [];
      } else {
        const res = await backend.patch(`purchaseOrder/${po._id}/convertir-en-oc`, {
          selectedItemIds: selectedItems.map(it => it._id),
          itemPrices: Object.fromEntries(items.map(it => [it._id, Number(it.pUnit) || 0])),
        });
        await subirArchivo(pdfFile, `Cotización proveedor ${po.provider?.name || po.providerName || ""}`);
        actualizado = res.data; noAprobados = noSeleccionados;
      }
      if (noAprobados.length && window.confirm(
        `Quedaron ${noAprobados.length} ítem(s) sin comprar:\n· ${noAprobados.map(x => x.description).join("\n· ")}\n\n¿Crear una solicitud de cotización nueva solo con esos ítems para otro proveedor?`
      )) {
        const dup = await backend.post(`purchaseOrder/${po._id}/duplicar-para-otro-proveedor`, { itemIds: noAprobados.map(x => x._id) });
        alert(`Solicitud N°${dup.data.duplicada.number} creada con ${noAprobados.length} ítem(s). Asígnele proveedor y envíela.`);
      }
      onSaved?.(actualizado);
    } catch (e) {
      alert("Error: " + (e?.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  const th = (extra = {}) => ({ padding:"6px 8px", fontSize:10, color:"#aaa", textAlign:"left", textTransform:"uppercase", letterSpacing:".04em", ...extra });
  const r = propuesta?.resumen;

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:"#1e1e1e", border:"1px solid #2e2e2e", borderRadius:8, padding:18,
        width: comparando ? "min(1280px, 98vw)" : propuesta ? "min(1080px, 97vw)" : "min(780px, 96vw)", maxHeight:"90vh", overflowY:"auto",
        color:"#ddd", fontSize:12, fontFamily:"Arial, sans-serif",
      }}>
        <div style={{ fontSize:14, fontWeight:700, marginBottom:4 }}>Cotización del proveedor</div>
        <div style={{ fontSize:11, color:"#cbd5e1", marginBottom:12 }}>
          Solicitud N°{po.number} · {po.provider?.name || po.providerName || "sin proveedor asignado"}
        </div>

        {/* Leer el archivo del proveedor */}
        <div style={{ background:"#111", border:"1px solid #2e2e2e", borderRadius:6, padding:"10px 12px", marginBottom:12 }}>
          <div style={{ fontSize:10, color:"#cbd5e1", textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>
            Leer la cotización que respondió el proveedor (PDF o foto)
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <label style={{ flex:1, minWidth:200, padding:"7px 10px", background:"#161616", border:"1px dashed #444", borderRadius:5,
                            color: pdfFile ? "#2ecc71" : "#888", fontSize:11, cursor:"pointer", textAlign:"center", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              <input type="file" accept="application/pdf,image/*" onChange={e => setPdfFile(e.target.files?.[0] || null)} style={{ display:"none" }} />
              {pdfFile ? `✓ ${pdfFile.name}` : "Seleccionar archivo (PDF, JPG, PNG)"}
            </label>
            <button type="button" onClick={handleLeer} disabled={leyendo || !pdfFile}
              style={{ background:"#0d1a2d", color:"#3498db", border:"1px solid #0d2a4a", borderRadius:5, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", opacity: (leyendo || !pdfFile) ? .6 : 1 }}>
              {leyendo ? "Leyendo… (30 s aprox.)" : "🔍 Leer y comparar"}
            </button>
          </div>
          {!propuesta && correos.some(m => (m.delProveedor || m.citaSolicitud) && !m.yaLeido) && (
            <div style={{ marginTop:10, background:"#0d1f10", border:"1px solid #14532d", borderRadius:6, padding:"8px 10px", fontSize:11, color:"#2ecc71" }}>
              <b>✉ Respuesta del proveedor detectada.</b> Revise y compare con lo solicitado: elija el correo de abajo y "Leer y comparar".
            </div>
          )}
          {correos.length > 0 && (
            <div style={{ marginTop:10 }}>
              <div style={{ fontSize:10, color:"#8a8a8a", textTransform:"uppercase", letterSpacing:".05em", marginBottom:4 }}>
                O léala directo de lo recibido en recepcion@ (sin descargar nada)
              </div>
              {correos.slice(0, 6).map(m => {
                const adj = m.adjuntos.find(a => /pdf/i.test(a.tipo)) || m.adjuntos[0];
                const destacado = m.delProveedor || m.citaSolicitud;
                return (
                  <div key={m._id} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 8px", borderRadius:5, marginBottom:3,
                                            background: destacado ? "#0d1f10" : "#161616", border: `1px solid ${destacado ? "#14532d" : "#2a2a2a"}` }}>
                    <div style={{ flex:1, minWidth:0, fontSize:11 }}>
                      <div style={{ color: destacado ? "#2ecc71" : "#ccc", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {m.asunto || "(sin asunto)"}{m.delProveedor ? " · del proveedor asignado" : ""}{m.yaLeido ? " · ya leída" : ""}
                      </div>
                      <div style={{ color:"#8a8a8a", fontSize:10, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {new Date(m.fecha).toLocaleString("es-CL", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })} · {m.remitente} · {adj?.nombre}
                      </div>
                    </div>
                    <button type="button" onClick={() => handleLeerCorreo(m, adj)} disabled={!!leyendoMail || leyendo}
                      style={{ background:"#0d1a2d", color:"#3498db", border:"1px solid #0d2a4a", borderRadius:5, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", opacity: leyendoMail ? .6 : 1 }}>
                      {leyendoMail === m._id ? "Leyendo…" : "🔍 Leer y comparar"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {errorLectura && <div style={{ marginTop:8, fontSize:11, color:"#e74c3c" }}>{errorLectura}</div>}
          {propuesta && (
            <div style={{ marginTop:10, display:"flex", gap:14, flexWrap:"wrap", fontSize:11, color:"#cbd5e1" }}>
              <span><b style={{ color:"#fff" }}>{propuesta.proveedor?.nombre || "Proveedor"}</b>{propuesta.numero ? ` · Cot. N°${propuesta.numero}` : ""}{propuesta.fecha ? ` · ${propuesta.fecha}` : ""}{propuesta.proveedor?.vendedor ? ` · ${propuesta.proveedor.vendedor}` : ""}</span>
              {r && <span style={{ color: NIVEL_COLOR.verde }}>{r.cotizadas} de {r.lineasSolicitadas} cotizadas</span>}
              {r?.faltantes > 0 && <span style={{ color: NIVEL_COLOR.rojo }}>{r.faltantes} faltante{r.faltantes > 1 ? "s" : ""}</span>}
              {r?.conVariacion > 0 && <span style={{ color: NIVEL_COLOR.amarillo }}>{r.conVariacion} con variación</span>}
              {r?.extras > 0 && <span style={{ color: NIVEL_COLOR.amarillo }}>{r.extras} línea(s) no pedidas</span>}
              {propuesta.totales?.total > 0 && <span>Total proveedor: {fmtCLP0(propuesta.totales.subtotal)} neto · {fmtCLP0(propuesta.totales.total)} con IVA</span>}
              {propuesta.condiciones?.length > 0 && <span style={{ color:"#8a8a8a" }}>{propuesta.condiciones.filter(c => !/^condiciones comerciales$/i.test(c)).join(" · ")}</span>}
            </div>
          )}
        </div>

        {puedeComparar && (
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10, flexWrap:"wrap" }}>
            {[["una", "Esta cotización"], ["comparar", `Comparar proveedores (${comparacion.proveedores.length})`]].map(([k, t]) => (
              <button key={k} type="button" onClick={() => setVista(k)}
                style={{ background: vista === k ? "#241a35" : "transparent", color: vista === k ? "#a78bfa" : "#aaa",
                         border: `1px solid ${vista === k ? "#3d2659" : "#2e2e2e"}`, borderRadius:5, padding:"5px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                {t}
              </button>
            ))}
            {vista === "una" && comparacion?.conclusion?.titulo && (
              <span style={{ fontSize:11, color:"#2ecc71" }}>La misma lista se cotizó a {comparacion.proveedores.length} proveedores · {comparacion.conclusion.titulo}</span>
            )}
          </div>
        )}

        {comparando && (
          <ComparadorProveedores comp={comparacion} onAdjudicado={onAdjudicado || onClose} />
        )}

        {!comparando && (<>
        <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:12 }}>
          <thead>
            <tr style={{ background:"#1a1a1a" }}>
              <th style={th({ textAlign:"center", width:30 })} title="Aprobar / comprar">✓</th>
              <th style={th()}>Solicitado</th>
              {propuesta && <th style={th()}>Cotizado por el proveedor</th>}
              {propuesta && <th style={th({ width:"24%" })}>Variación</th>}
              {!propuesta && <th style={th({ textAlign:"right", width:60 })}>Cant.</th>}
              <th style={th({ textAlign:"right", width:120 })}>$ / {propuesta ? "trozo" : "unidad"}</th>
              <th style={th({ textAlign:"right", width:110 })}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const sub = (Number(it.pUnit) || 0) * (Number(it.quantity) || 0);
              const color = it.nivel ? NIVEL_COLOR[it.nivel] : "#444";
              const cambioDesc = it.description !== it.descripcionOriginal;
              return (
                <tr key={it._id || idx} style={{ borderBottom:"1px solid #1f1f1f", opacity: it.selected ? 1 : 0.45, borderLeft: `3px solid ${color}` }}>
                  <td style={{ padding:"6px 8px", textAlign:"center" }}>
                    <input type="checkbox" checked={it.selected}
                      onChange={e => setItems(prev => prev.map((x, i) => i === idx ? { ...x, selected: e.target.checked } : x))}
                      style={{ cursor:"pointer", accentColor:"#a78bfa" }} />
                  </td>
                  <td style={{ padding:"6px 8px", color:"#ddd" }}>
                    <div><MedidaConMm texto={it.descripcionOriginal} /></div>
                    <div style={{ fontSize:10, color:"#8a8a8a" }}>{Number(it.quantity) || 0} {it.unit}{cambioDesc ? <span style={{ color: NIVEL_COLOR.amarillo }}> · en la OC quedará: <MedidaConMm texto={it.description} /></span> : null}</div>
                    {(it.terminada || it.notes) && (
                      <div style={{ fontSize:10, color:"#a3a3a3", marginTop:2 }} title={it.notes}>
                        {it.terminada ? <span style={{ color:"#cbd5e1" }}>Pieza terminada {it.terminada}</span> : null}
                        {it.terminada && it.notes ? " · " : ""}
                        {it.notes ? it.notes.replace(/\s*·?\s*pieza terminada[^·]*/i, "").trim() : ""}
                      </div>
                    )}
                  </td>
                  {propuesta && (
                    <td style={{ padding:"6px 8px", color:"#cbd5e1" }}>
                      {it.cotizado ? (
                        <>
                          <div>{it.cotizado.material} · <MedidaConMm texto={it.cotizado.diametro} />{it.cotizado.codigo ? <span style={{ color:"#666" }}> · {it.cotizado.codigo}</span> : null}</div>
                          <div style={{ fontSize:10, color:"#8a8a8a" }}>
                            {it.cotizado.trozos != null ? `${it.cotizado.trozos} tr` : ""}{it.cotizado.largoMm ? ` × ${it.cotizado.largoMm} mm` : ""}
                            {it.cotizado.cantidad ? ` · ${it.cotizado.cantidad} ${it.cotizado.unidad || ""}` : ""}
                            {it.cotizado.precioUnitario ? ` a ${fmtCLP0(it.cotizado.precioUnitario)}/${(it.cotizado.unidad || "un").replace(/s$/i, "").toLowerCase()}` : ""}
                            {it.cotizado.valorTotal ? ` = ${fmtCLP0(it.cotizado.valorTotal)}` : ""}
                          </div>
                        </>
                      ) : <span style={{ color: NIVEL_COLOR.rojo }}>No cotizado</span>}
                    </td>
                  )}
                  {propuesta && (
                    <td style={{ padding:"6px 8px" }}>
                      {it.variaciones.length === 0
                        ? <span style={{ fontSize:10, color: NIVEL_COLOR.verde }}>Coincide</span>
                        : it.variaciones.map((v, i) => (
                          <div key={i} style={{ fontSize:10, color: NIVEL_COLOR[v.nivel] || "#ccc", marginBottom:2 }}>{v.texto}</div>
                        ))}
                    </td>
                  )}
                  {!propuesta && <td style={{ padding:"6px 8px", color:"#cbd5e1", textAlign:"right" }}>{it.quantity}</td>}
                  <td style={{ padding:"6px 8px" }}>
                    <input type="number" min="0" step="any" value={it.pUnit}
                      onChange={e => setItems(prev => prev.map((x, i) => i === idx ? { ...x, pUnit: e.target.value } : x))}
                      style={{ width:"100%", background:"#161616", border:"1px solid #333", borderRadius:4, padding:"5px 8px", color:"#ddd", fontSize:12, fontFamily:"monospace", textAlign:"right" }} />
                  </td>
                  <td style={{ padding:"6px 8px", color: sub > 0 ? "#2ecc71" : "#666", fontWeight:600, textAlign:"right" }}>
                    {sub > 0 ? fmtCLP0(sub) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background:"#1a1a1a" }}>
              <td colSpan={propuesta ? 4 : 4} style={{ padding:"8px 10px", textAlign:"right", color:"#aaa", textTransform:"uppercase", fontSize:10, fontWeight:700 }}>
                Aprobado ({selectedItems.length} de {items.length}) · neto
              </td>
              <td colSpan={2} style={{ padding:"8px 10px", textAlign:"right", color: total > 0 ? "#2ecc71" : "#666", fontWeight:700 }}>
                {fmtCLP0(total)} <span style={{ color:"#8a8a8a", fontWeight:400, fontSize:10 }}>· IVA {fmtCLP0(total * 0.19)} · total {fmtCLP0(total * 1.19)}</span>
              </td>
            </tr>
          </tfoot>
        </table>

        {propuesta?.extras?.length > 0 && (
          <div style={{ fontSize:11, color: NIVEL_COLOR.amarillo, marginBottom:12 }}>
            El proveedor cotizó además líneas que no pedimos: {propuesta.extras.map(x => `${x.descripcion}${x.trozos ? ` (${x.trozos} tr${x.largoMm ? ` × ${x.largoMm} mm` : ""})` : ""} ${fmtCLP0(x.valorTotal)}`).join("; ")}.
          </div>
        )}

        <div style={{ fontSize:10, color:"#cbd5e1", marginBottom:12, lineHeight:1.4 }}>
          <b>Guardar precios:</b> la solicitud queda "Cotizada" con todos los ítems; los aprobados con su precio. Sirve para comparar con otro proveedor antes de decidir.<br/>
          <b>Generar OC:</b> se compra solo lo aprobado y pasa a "Pedido". Lo no aprobado se puede mandar a otro proveedor en una solicitud nueva.
          {propuesta && <><br/>Las líneas en amarillo son equivalentes comerciales (por ejemplo Ø4" en vez de Ø100 mm): si las aprueba, la OC sale con la medida cotizada.</>}
        </div>

        {po.rfqGroup && (
          <ComparadorRfq rfqGroup={po.rfqGroup} poActualId={po._id} onCambio={onClose} />
        )}
        </>)}

        <div style={{ background:"#111", border:"1px solid #2e2e2e", borderRadius:6, padding:"10px 12px", marginBottom:12 }}>
          <div style={{ fontSize:10, color:"#cbd5e1", textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>
            Archivos de la cotización del proveedor
          </div>
          <DocumentAttacher refField="purchaseOrderRefs" refId={po._id} defaultType="cotizacionProveedor" />
        </div>

        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, flexWrap:"wrap" }}>
          <button onClick={onClose} disabled={saving}
            style={{ background:"transparent", color:"#cbd5e1", border:"1px solid #444", borderRadius:5, padding:"6px 14px", fontSize:12, cursor:"pointer" }}>Cancelar</button>
          {!comparando && <>
          <button onClick={handleGuardar} disabled={saving || leyendo}
            style={{ background:"#a78bfa", color:"#241a35", border:"none", borderRadius:5, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", opacity: saving ? .6 : 1 }}>
            {saving ? "Guardando…" : "💾 Guardar precios"}
          </button>
          <button onClick={handleGenerarOC} disabled={saving || leyendo || selectedItems.length === 0 || total === 0}
            style={{ background:"#2ecc71", color:"#0d1f10", border:"none", borderRadius:5, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", opacity: (saving || selectedItems.length === 0 || total === 0) ? .6 : 1 }}>
            {saving ? "Procesando…" : `✓ Generar OC con lo aprobado (${selectedItems.length})`}
          </button>
          </>}
        </div>
      </div>
    </div>
  );
};

// D-Compras-Fase2 — Panel de historial con selector de mes.
// Cuando se integre SII RCV Compras, los meses sin actividad se llenan
// automático con las facturas recibidas vía API SII (no manual).
const HistorialPanel = ({ historial, historialMonth, setHistorialMonth, fmtM, fmtD, padOC, hIn, hOut, onVerPdf, pdfLoadingId }) => {
  const SS = {
    th: { padding:"6px 10px", fontSize:9, fontWeight:700, color: "#aaa", textTransform:"uppercase", letterSpacing:".06em", borderBottom:"1px solid #2e2e2e", textAlign:"left", whiteSpace:"nowrap" },
    td: { padding:"7px 10px", fontSize:12, color:"#bbb", borderBottom:"1px solid #1f1f1f" },
  };

  // Lista de meses disponibles: desde 2025-01 hasta el mes actual + 1.
  // Aún si no hay datos, muestra el rango completo (refleja que cuando se
  // integre SII se llenará automático).
  const monthOptions = React.useMemo(() => {
    const now = new Date();
    const out = [{ value: "all", label: "Todo el historial" }];
    const monthLabels = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    for (let y = now.getFullYear(); y >= 2025; y--) {
      const fromM = (y === 2025) ? 0 : 11;
      const toM   = (y === now.getFullYear()) ? now.getMonth() : 11;
      for (let m = toM; m >= fromM; m--) {
        const ym = `${y}-${String(m+1).padStart(2,"0")}`;
        out.push({ value: ym, label: `${monthLabels[m]} ${y}` });
      }
    }
    return out;
  }, []);

  const filtered = React.useMemo(() => {
    if (historialMonth === "all") return historial;
    const [y, m] = historialMonth.split("-").map(Number);
    const start = new Date(Date.UTC(y, m-1, 1));
    const end   = new Date(Date.UTC(y, m, 1));
    return historial.filter(p => {
      const d = p.receivedDate || p.updatedAt;
      if (!d) return false;
      const dd = new Date(d);
      return dd >= start && dd < end;
    });
  }, [historial, historialMonth]);

  const totalMes = filtered
    .filter(p => p.status === "recibido")
    .reduce((s, p) => s + (Number(p.invoiceAmount) || Number(p.total) || 0), 0);

  return (
    <div>
      {/* Toolbar histórico: selector mes + total */}
      <div style={{
        display:"flex", justifyContent:"space-between", alignItems:"center",
        marginBottom:10, gap:10, flexWrap:"wrap",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:10, color: "#aaa", textTransform:"uppercase", letterSpacing:".05em" }}>Mes:</span>
          <select
            value={historialMonth}
            onChange={e => setHistorialMonth(e.target.value)}
            style={{
              background:"#161616", border:"1px solid #333", borderRadius:5,
              padding:"6px 10px", fontSize:12, color:"#ddd",
              fontFamily:"Arial,sans-serif", cursor:"pointer",
            }}
          >
            {monthOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {historialMonth !== "all" && filtered.length === 0 && (
            <span style={{ fontSize:10, color: "#cbd5e1", fontStyle:"italic" }}>
              Sin compras este mes — se llenará auto cuando se active SII RCV
            </span>
          )}
        </div>
        {historialMonth !== "all" && totalMes > 0 && (
          <div style={{ fontSize:11, color: "#aaa" }}>
            Total comprado en el mes:{" "}
            <strong style={{ color:"#16a085" }}>{fmtM(totalMes)}</strong>
            {" · "}{filtered.filter(p => p.status === "recibido").length} factura{filtered.filter(p => p.status === "recibido").length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div style={{ border:"1px solid #2e2e2e", borderRadius:8, overflow:"hidden", background:"#242424" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
          <thead>
            <tr>
              <th style={{ ...SS.th, width:110 }}>N° OC</th>
              <th style={{ ...SS.th, width:180 }}>Proveedor</th>
              <th style={SS.th}>Ítems</th>
              <th style={{ ...SS.th, width:110 }}>Total</th>
              <th style={{ ...SS.th, width:110 }}>N° Factura</th>
              <th style={{ ...SS.th, width:110 }}>Estado</th>
              <th style={{ ...SS.th, width:100 }}>Fecha recep.</th>
              <th style={{ ...SS.th, width:60 }}>PDF</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ ...SS.td, textAlign:"center", color: "#999", padding:"24px 0", fontStyle:"italic" }}>
                {historialMonth === "all"
                  ? "Sin historial todavía"
                  : "Sin compras en este mes (se completará automático al integrar SII RCV)"}
              </td></tr>
            ) : filtered.map((p, i) => (
              <tr key={p._id || i} onMouseEnter={hIn} onMouseLeave={hOut}>
                <td style={{ ...SS.td, fontFamily:"monospace", color: "#bbb" }}>{padOC(p.number)}</td>
                <td style={{ ...SS.td, color:"#ddd" }}>{p.providerName || p.provider?.name || "—"}</td>
                <td style={{ ...SS.td, color: "#cbd5e1", fontSize:11 }}>
                  {(p.items||[]).map(it => it.description).filter(Boolean).join(", ").slice(0,70) || "—"}
                </td>
                <td style={{ ...SS.td, color:"#2ecc71", fontWeight:600 }}>{fmtM(p.total)}</td>
                <td style={{ ...SS.td, fontFamily:"monospace", color: "#bbb" }}>{p.invoiceNumber || "—"}</td>
                <td style={{ ...SS.td, overflow:"visible" }}><Badge status={p.status} /></td>
                <td style={SS.td}>{fmtD(p.receivedDate || p.updatedAt)}</td>
                <td style={SS.td}>
                  <button
                    onClick={() => onVerPdf(p)}
                    disabled={pdfLoadingId === p._id}
                    title="PDF oficial de la OC (generado por el servidor)"
                    style={{ fontSize:10, padding:"3px 8px", border:"1px solid #14532d", background:"transparent", color:"#2ecc71", borderRadius:4, cursor:"pointer", opacity: pdfLoadingId === p._id ? 0.6 : 1 }}
                  >
                    {pdfLoadingId === p._id ? "…" : "↓ PDF"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Compras Fase 1 — bandeja "compras sin registro" (RCV del SII sin OC) ────
// Red de seguridad del módulo: muestra toda factura de proveedor que llegó por
// el SII y no tiene OC que la respalde. Los gastos generales (arriendo, luz…)
// quedan fuera del módulo y solo se informan como total aparte.
const DOC_LABEL = { 33:"Factura", 34:"F. exenta", 46:"F. compra", 52:"Guía", 56:"N. débito", 61:"N. crédito" };
const CAT_COLOR = {
  materiaPrima:      { color:"#3498db", bg:"#0d1a2d" },
  consumibles:       { color:"#f39c12", bg:"#2d2000" },
  serviciosExternos: { color:"#a78bfa", bg:"#241a35" },
  herramientas:      { color:"#2ecc71", bg:"#0d2a15" },
  otrosTaller:       { color:"#cbd5e1", bg:"#1f1f1f" },
};

const SinRegistroPanel = ({ fmtM, fmtD, hIn, hOut }) => {
  const now = new Date();
  const mesActual = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
  const [periodo, setPeriodo] = React.useState(mesActual);
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let vivo = true;
    setLoading(true);
    backend.get(`purchaseOrder/sin-registro${periodo !== "all" ? `?periodo=${periodo}` : ""}`)
      .then(res => { if (vivo) setData(res.data); })
      .catch(e => console.error("[SinRegistro]", e))
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [periodo]);

  const SS = {
    th: { padding:"6px 10px", fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:".06em", borderBottom:"1px solid #2e2e2e", textAlign:"left", whiteSpace:"nowrap" },
    td: { padding:"7px 10px", fontSize:12, color:"#bbb", borderBottom:"1px solid #1f1f1f", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
    chip: { padding:"8px 14px", borderRadius:8, border:"1px solid #2e2e2e", background:"#242424", minWidth:150 },
  };

  const monthOptions = React.useMemo(() => {
    const out = [{ value:"all", label:"Todos los períodos" }];
    const labels = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    for (let y = now.getFullYear(); y >= 2026; y--) {
      const toM = (y === now.getFullYear()) ? now.getMonth() : 11;
      for (let m = toM; m >= 0; m--) out.push({ value:`${y}-${String(m+1).padStart(2,"0")}`, label:`${labels[m]} ${y}` });
    }
    return out;
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const t = data?.totales;
  const filas = data?.sinRegistro || [];

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, color:"#aaa", textTransform:"uppercase", letterSpacing:".05em" }}>Período:</span>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)}
          style={{ background:"#161616", border:"1px solid #333", borderRadius:5, padding:"6px 10px", fontSize:12, color:"#ddd", cursor:"pointer" }}>
          {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {t && (
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginLeft:"auto" }}>
            <div style={SS.chip}>
              <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:".05em" }}>Sin registro (taller)</div>
              <div style={{ fontSize:15, fontWeight:700, color: t.sinRegistro.docs ? "#e74c3c" : "#2ecc71" }}>
                {t.sinRegistro.docs} docs · {fmtM(t.sinRegistro.neto)}
              </div>
            </div>
            <div style={SS.chip}>
              <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:".05em" }}>Con OC (conciliadas)</div>
              <div style={{ fontSize:15, fontWeight:700, color:"#2ecc71" }}>{t.conOc} docs</div>
            </div>
            <div style={SS.chip} title="Arriendo, servicios básicos, administración — fuera del módulo de compras; van a contabilidad.">
              <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:".05em" }}>Gastos generales (fuera)</div>
              <div style={{ fontSize:15, fontWeight:700, color:"#999" }}>
                {t.gastosGenerales.docs} docs · {fmtM(t.gastosGenerales.neto)}
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ border:"1px solid #2e2e2e", borderRadius:8, overflow:"hidden", background:"#242424" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
          <thead>
            <tr>
              <th style={{ ...SS.th, width:90  }}>Fecha</th>
              <th style={{ ...SS.th, width:80  }}>Folio</th>
              <th style={{ ...SS.th, width:85  }}>Doc</th>
              <th style={SS.th}>Proveedor</th>
              <th style={{ ...SS.th, width:150 }}>Categoría</th>
              <th style={{ ...SS.th, width:110 }}>Neto</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ ...SS.td, textAlign:"center", color:"#999", padding:"24px 0" }}>Cargando…</td></tr>
            ) : filas.length === 0 ? (
              <tr><td colSpan={6} style={{ ...SS.td, textAlign:"center", color:"#2ecc71", padding:"24px 0" }}>
                ✓ Todas las compras del período tienen OC (o no hay compras del taller)
              </td></tr>
            ) : filas.map((f) => {
              const cc = CAT_COLOR[f.categoria] || CAT_COLOR.otrosTaller;
              return (
                <tr key={f._id} onMouseEnter={hIn} onMouseLeave={hOut}>
                  <td style={SS.td}>{fmtD(f.fecha)}</td>
                  <td style={{ ...SS.td, fontFamily:"monospace", color:"#bbb" }}>{f.folio}</td>
                  <td style={{ ...SS.td, color:"#cbd5e1" }}>{DOC_LABEL[f.tipoDoc] || f.tipoDoc || "—"}</td>
                  <td style={{ ...SS.td, color:"#fff", fontWeight:600 }} title={f.rut}>{f.proveedor || f.rut || "—"}</td>
                  <td style={{ ...SS.td, overflow:"visible" }}>
                    <span style={{ display:"inline-block", fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20, background:cc.bg, color:cc.color, border:`1px solid ${cc.color}33` }}>
                      {f.categoriaLabel || f.categoria}
                    </span>
                  </td>
                  <td style={{ ...SS.td, color:"#e74c3c", fontWeight:600 }}>{fmtM(f.montoNeto)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize:10, color:"#888", marginTop:8, fontStyle:"italic" }}>
        Facturas de proveedor recibidas en el SII sin una OC que las respalde. La conciliación corre
        automática con cada sync SII (por folio de factura anotado al recibir, o RUT + monto ±5% + fecha).
      </div>
    </div>
  );
};

export default Purchases;
